var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/vault.ts
import { z } from "zod";
var vaultValueSchema = z.object({
  c: z.string().describe("encrypted content"),
  h: z.string().describe("sha256 hash of the encryption key + optional password"),
  b: z.boolean().describe("burn after reading"),
  dt: z.string().describe("delete token"),
  ttl: z.number().describe("time to live (TTL) in milliseconds"),
  cd: z.number().describe("created date time"),
  ips: z.string().describe("ip/cidr allow-list").optional(),
  rc: z.number().describe("maximum number of times the secret can be read").optional(),
  fc: z.number().min(1).max(10).describe("burn after n failed attempts").optional(),
  wh: z.object({
    u: z.string().url().describe("url of the webhook"),
    n: z.string().max(50).describe("name of the secret").optional(),
    r: z.boolean().default(true).describe("should the webhook be called on read"),
    fpk: z.boolean().default(false).describe("should the webhook be called for failure to read based on password or key"),
    fip: z.boolean().default(false).describe("should the webhook be called for failure to read based on ip address"),
    b: z.boolean().default(false).describe("should the webhook be called for secret burn")
  }).describe("webhook configuration").optional()
});

// src/api.ts
import { z as z2 } from "zod";
var createVaultRequestSchema = vaultValueSchema.omit({
  cd: true,
  dt: true
});
var createVaultResponseSchema = z2.object({
  id: z2.string().describe("vault id"),
  dt: z2.string().describe("delete token")
});
var readVaultParamsSchema = z2.object({
  vaultId: z2.string()
});
var readVaultQuerySchema = z2.object({
  h: z2.string().describe("sha512 hash of the encryption key + optional password")
});
var readVaultResponseSchema = z2.object({
  c: z2.string().describe("encrypted content"),
  b: z2.boolean().describe("burn after reading"),
  ttl: z2.number().describe("time to live (TTL) in milliseconds"),
  cd: z2.number().describe("created date time")
});
var deleteVaultParamsSchema = z2.object({
  vaultId: z2.string()
});
var deleteVaultRequestSchema = z2.object({
  dt: z2.string()
});

// src/i18n/locales/en.ts
var en = {
  common: {
    createSecret: "Create Secret",
    starOnGithub: "Star on GitHub",
    features: "Features",
    learnMore: "Learn More",
    loading: "Loading...",
    create: "Create",
    delete: "Delete",
    copy: "Copy",
    share: "Share",
    download: "Download",
    success: "Success!",
    error: "Error",
    confirm: "Confirm",
    requestNewLanguage: "Request new or fix translations",
    footer: {
      tagline: "Built with security and privacy in mind - because ignorance can be bliss"
    },
    header: {
      tagline: "Ephemeral data sharing with zero-knowledge <aesLink>AES-256</aesLink> <e2eLink>end-to-end encryption</e2eLink>"
    },
    time: {
      minute_one: "{{count}} minute",
      minute_other: "{{count}} minutes",
      hour_one: "{{count}} hour",
      hour_other: "{{count}} hours",
      day_one: "{{count}} day",
      day_other: "{{count}} days"
    }
  },
  landing: {
    title: "Secure Sensitive Data Sharing",
    subtitle: "crypt.fyi enables zero-knowledge end-to-end encrypted sensitive data sharing with automatic deletion",
    features: {
      encryption: {
        title: "End-to-end Encryption",
        description: "Secure your data with AES-256-GCM encryption before it leaves your browser"
      },
      security: {
        title: "Enhanced Security",
        description: "Strict Content Security Policy (CSP) and rate limits to mitigate XSS and brute-force attacks"
      },
      zeroKnowledge: {
        title: "Zero-knowledge",
        description: "Our servers never see your unencrypted data - only you and your recipient have access"
      },
      burn: {
        title: "Burn After Reading",
        description: "Automatically destroy secrets after they are accessed for maximum security"
      },
      failedAttempts: {
        title: "Burn After Failed Attempts",
        description: "Automatically destroy secrets after a number of failed access attempts for enhanced security"
      },
      expiration: {
        title: "Auto-Expiration",
        description: "Set custom expiration times to ensure secrets don't persist longer than needed"
      },
      password: {
        title: "Password Protection",
        description: "Add an extra layer of security with optional password protection"
      },
      files: {
        title: "File Sharing",
        description: "Securely share files with simple drag and drop functionality"
      },
      webhooks: {
        title: "Webhooks",
        description: "Get notified when your secrets are accessed, burned, or fail to be read"
      },
      ipControl: {
        title: "IP Restrictions",
        description: "Control access by limiting which IP addresses or CIDR ranges can view your secrets"
      },
      readLimits: {
        title: "Read Limits",
        description: "Set maximum view counts to restrict how many times a secret can be accessed"
      },
      qrCode: {
        title: "QR Codes",
        description: "Generate QR codes for easy mobile sharing of your secret URLs"
      },
      cli: {
        title: "CLI Tool",
        description: "Automate secret sharing with our command-line interface"
      },
      chromeExtension: {
        title: "Chrome Extension",
        description: "Share secrets directly from your browser in-context with our Chrome extension"
      },
      docker: {
        title: "Docker Support",
        description: "Deploy your own instance using our official Docker images"
      }
    },
    steps: {
      encrypt: {
        title: "1. Encrypt",
        description: "Your secret is encrypted in your browser before being stored"
      },
      share: {
        title: "2. Share",
        description: "Send the secure link to your intended recipient"
      },
      autoDelete: {
        title: "3. Auto-Delete",
        description: "Secret is permanently deleted after being viewed"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Secret content",
        placeholder: "Enter your secret content or file here...",
        fileHint: "add a file by drag-n-drop or clicking here",
        fileSelected: "File selected: {{name}} ({{size}} KB)"
      },
      password: {
        label: "Password",
        placeholder: "Optional (but recommended)"
      },
      ttl: {
        label: "Time to live",
        placeholder: "Select expiration time"
      },
      burn: {
        label: "Burn after reading",
        description: "Guarantees only one recipient can access the secret"
      },
      advanced: {
        toggle: "advanced configuration",
        ip: {
          label: "IP/CIDR allow-list",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restrict access to specific IP addresses or CIDR blocks (comma separated)"
        },
        readCount: {
          label: "Read count",
          description: "Maximum number of times the secret can be read"
        },
        failedAttempts: {
          label: "Burn after failed attempts",
          description: "Automatically delete the secret after a number of failed access attempts"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "Webhook URL to call when the secret is read, burned, or fails to read (configurable, if set)",
          read: "Read",
          burn: "Burn",
          failureToReadPK: "Failure (pass or key)",
          failureToReadIP: "Failure (IP or CIDR)",
          nameLabel: "Name",
          namePlaceholder: "Enter a name for this webhook",
          nameDescription: "A name to identify this webhook in notifications"
        }
      }
    },
    success: {
      title: "Secret Created!",
      description: {
        main: "Your secret has been created and the URL has been copied to your clipboard",
        password: "Share the URL and password with the desired recipient"
      },
      urlCopied: "URL copied to clipboard",
      qrCode: {
        title: "Secret URL QR Code",
        description: "Download and share the secret URL QR Code"
      },
      createAnother: "Create Another",
      deleteSecret: "Delete Secret",
      info: {
        expires: "Expires in: {{time}}",
        burn: "Secret will be deleted after it is viewed",
        passwordProtected: "Password protected",
        ipRestrictions: "IP restriction(s): {{ips}}",
        readCount: "Read count: {{count}}",
        webhook: "Webhook configured for: {{events}} ({{url}})",
        failureCount: "Secret will be deleted after {{count}} failed attempts"
      }
    },
    errors: {
      contentRequired: "Content is required",
      readCountWithBurn: "Read count cannot be used with burn after reading",
      tooManyIps: "Too many IP restrictions (max {{max}})",
      invalidIp: "Invalid IP address or CIDR block: {{ip}}",
      uploadFailed: "Failed to process dropped content: {{error}}",
      deleteFailed: "Failed to delete secret: {{error}}",
      secretNotFound: "secret not found",
      unexpectedStatus: "unexpected status code {{code}}",
      webhookConfigInvalid: "Webhook configuration is invalid - at least one webhook event type is required",
      fileSizeExceeded: "File size exceeded maximum allowed size",
      fileReadError: "Failed to read file",
      fileReadAborted: "File reading was aborted"
    }
  },
  view: {
    notFound: {
      title: "Secret Not Found",
      description: "This secret may have expired or been deleted.",
      createNew: "Create New Secret"
    },
    password: {
      title: "Enter Password",
      placeholder: "Enter the password",
      description: "This secret is protected with a password - request from the sender",
      error: "Incorrect password"
    },
    content: {
      fileShared: "A file has been shared with you",
      downloadFile: "Download File",
      hideContent: "Hide content",
      showContent: "Show content",
      copyToClipboard: "Copy to clipboard",
      copiedToClipboard: "Secret copied to clipboard",
      clickToReveal: "Click the eye icon above to reveal the secret",
      passwordProtected: "This secret is password protected. Click to enter password.",
      clickToEnterPassword: "Click to enter password"
    },
    info: {
      burnedAfterReading: "This secret was deleted after your viewing and is no longer available after leaving the page.",
      expiresIn: "Expires {{time}}"
    },
    errors: {
      notFound: "not found",
      unexpectedStatus: "unexpected status code {{code}}"
    },
    actions: {
      viewSecret: "View Secret"
    }
  },
  about: {
    title: "About",
    intro: "crypt.fyi is a secure, open-source, ephemeral data-sharing platform that enables you to share sensitive information safely. Whether it's passwords, API keys, or confidential messages, crypt.fyi ensures your data remains private and automatically disappears after being accessed.",
    whyCryptFyi: {
      title: "Why crypt.fyi?",
      commonPractices: {
        title: "The Problem with Common Practices",
        description: "Every day, sensitive information like passwords, API keys, and private data is shared through insecure channels in plain text:",
        problems: {
          email: "Email - can be intercepted, stored indefinitely, and forwarded without control",
          slack: "Slack/Teams messages - remain in chat history and company logs",
          sms: "SMS/Text messages - stored on multiple devices and carrier servers",
          messaging: "Instant messaging - often lacks proper encryption and data deletion"
        }
      },
      existingSolutions: {
        title: "Existing Solutions and Their Limitations",
        description: "While there are other tools in this space, each has its limitations:",
        limitations: {
          onePassword: "1Password - excellent for team password management, but",
          onePasswordLink: "doesn't support external non-users sharing internally",
          otherTools: "PrivateBin/PwPush/OneTimeSecret - similar core functionality, but dated user interfaces and technology stacks as well as some missing",
          otherToolsConfigLink: "configurability",
          otherToolsSecurityLink: "security features"
        }
      },
      approach: {
        title: "The crypt.fyi Approach",
        description: "crypt.fyi was built to address these challenges while embracing modern web technologies. It combines the security principles of existing solutions with a clean, intuitive interface and a modern tech stack. The result is a tool that's both highly secure and pleasant to use."
      }
    },
    howItWorks: {
      title: "How It Works",
      steps: {
        encrypt: {
          title: "1. Encrypt",
          description: "Your secret is encrypted right in your browser before it ever leaves your device. Only people with the special link, that you've explicitly shared, can decrypt it."
        },
        share: {
          title: "2. Share",
          description: "Share the secure link with your intended recipient. The link contains everything needed to decrypt the message, unless a password is specified."
        },
        burn: {
          title: "3. Burn after read",
          description: "Once accessed, if 'burn after read' is checked, the secret is permanently deleted from our servers. No traces left behind."
        }
      }
    },
    security: {
      title: "Security Implementation",
      encryption: {
        title: "End-to-End Encryption",
        description: "All secrets are encrypted using AES-256-GCM encryption in your browser before transmission. The encryption key never leaves your device, ensuring true end-to-end encryption.",
        features: {
          key: "Encryption key is derived from a cryptographically secure random generation",
          derivation: "Key derivation uses PBKDF2 with SHA-256",
          vector: "Each secret has a unique initialization vector (IV)"
        }
      },
      zeroKnowledge: {
        title: "Zero-Knowledge Architecture",
        description: "Our servers never see your unencrypted data. We employ a zero-knowledge architecture where:",
        features: {
          clientSide: "All encryption/decryption happens client-side",
          storage: "Servers only store encrypted data",
          keys: "Encryption keys are transmitted via URL fragments, which never reach the backend api server"
        }
      },
      protection: {
        title: "Data Protection",
        description: "Multiple layers of security ensure your data remains protected:",
        features: {
          encryption: "Client-side encryption/decryption",
          tls: "TLS encryption for all API communications",
          destruction: "Automatic secret destruction after access",
          logging: "No server-side logging of sensitive data",
          password: "Optional password protection for additional security"
        }
      }
    },
    openSource: {
      title: "Open Source",
      description: "crypt.fyi is open source and auditable. You can review our code, submit issues, and contribute on"
    }
  },
  privacy: {
    title: "Privacy Policy",
    intro: "At crypt.fyi, we take your privacy seriously. This Privacy Policy explains how we handle your information when you use our zero-knowledge, end-to-end encrypted secret sharing platform.",
    doNotCollect: {
      title: "Information We Don't Collect",
      description: "Due to our zero-knowledge architecture, we are technically unable to access:",
      items: {
        secrets: "Your unencrypted secrets or files",
        keys: "Encryption keys or passwords",
        urls: "URL fragments containing decryption information",
        content: "The content of your encrypted data",
        recipients: "Information about the recipients of your secrets"
      }
    },
    collect: {
      title: "Information We Do Collect",
      description: "We collect and store only the minimum required information:",
      items: {
        encrypted: "Encrypted data (which we cannot decrypt)",
        hashes: "Key verification hashes (used to verify access rights without knowing the actual keys)",
        metadata: "Basic request metadata (IP addresses, timestamps) for rate limiting and abuse prevention",
        webhooks: "Optional webhook URLs if provided (for notifications about secret access and deletion)"
      },
      note: "All stored data is automatically deleted after expiration or when accessed (if burn after reading is enabled)."
    },
    usage: {
      title: "How We Use Information",
      description: "We use the collected information solely for:",
      items: {
        transmission: "Facilitating the secure transmission of your encrypted secrets",
        rateLimits: "Enforcing rate limits to prevent abuse",
        ipControl: "Implementing IP/CIDR allow-listing when configured",
        notifications: "Sending webhook notifications when enabled",
        security: "Maintaining system security and preventing unauthorized access"
      }
    },
    security: {
      title: "Data Storage and Security",
      description: "Our security measures include:",
      items: {
        encryption: "All encryption/decryption occurs in your browser using AES-256-GCM",
        csp: "Strict Content Security Policy (CSP) to prevent XSS attacks",
        tls: "TLS encryption for all API communications",
        expiration: "Automatic data expiration with configurable Time-To-Live (TTL)",
        deletion: "Secure deletion of data after access or expiration",
        storage: "No persistent storage of sensitive information"
      }
    },
    thirdParty: {
      title: "Third-Party Services",
      description: "We do not share any information with third parties except in the following cases:",
      items: {
        webhooks: "When you enable webhooks, we send notifications to the URLs you provide about secret access and deletion",
        infrastructure: "Infrastructure providers that host our services (who only see encrypted data they cannot decrypt)"
      }
    },
    rights: {
      title: "Your Rights and Choices",
      description: "You have control over your data:",
      items: {
        expiration: "Choose custom expiration times for your secrets",
        burn: "Enable burn after reading for immediate deletion after access",
        password: "Add password protection for additional security",
        ip: "Configure IP restrictions to control access",
        readLimits: "Set read count limits"
      }
    },
    changes: {
      title: "Changes to This Policy",
      description: "We may update this Privacy Policy from time to time. We will notify users of any material changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes."
    },
    contact: {
      title: "Contact Us",
      description: "If you have any questions about this Privacy Policy, you can contact us through our"
    }
  }
};

// src/i18n/locales/es.ts
var es = {
  common: {
    createSecret: "Crear Secreto",
    starOnGithub: "Estrella en GitHub",
    features: "Caracter\xEDsticas",
    learnMore: "M\xE1s Informaci\xF3n",
    loading: "Cargando...",
    create: "Crear",
    delete: "Eliminar",
    copy: "Copiar",
    share: "Compartir",
    download: "Descargar",
    success: "\xA1\xC9xito!",
    error: "Error",
    confirm: "Confirmar",
    requestNewLanguage: "Solicitar nuevo o corregir traducciones",
    footer: {
      tagline: "Construido con seguridad y privacidad en mente - porque la ignorancia puede ser una bendici\xF3n"
    },
    header: {
      tagline: "Compartir datos ef\xEDmeros con cifrado <aesLink>AES-256</aesLink> de <e2eLink>extremo a extremo</e2eLink> de conocimiento cero"
    },
    time: {
      minute_one: "{{count}} minuto",
      minute_other: "{{count}} minutos",
      hour_one: "{{count}} hora",
      hour_other: "{{count}} horas",
      day_one: "{{count}} d\xEDa",
      day_other: "{{count}} d\xEDas"
    }
  },
  landing: {
    title: "Compartir Datos Sensibles de Forma Segura",
    subtitle: "crypt.fyi permite el intercambio de datos sensibles cifrados de extremo a extremo con conocimiento cero y eliminaci\xF3n autom\xE1tica",
    features: {
      encryption: {
        title: "Cifrado de Extremo a Extremo",
        description: "Asegura tus datos con cifrado AES-256-GCM antes de que salgan de tu navegador"
      },
      security: {
        title: "Seguridad Mejorada",
        description: "Pol\xEDtica de seguridad de contenido (CSP) estricta y l\xEDmites de velocidad para mitigar ataques XSS y de fuerza bruta"
      },
      zeroKnowledge: {
        title: "Conocimiento Cero",
        description: "Nuestros servidores nunca ven tus datos sin cifrar - solo t\xFA y tu destinatario tienen acceso"
      },
      burn: {
        title: "Destruir al Leer",
        description: "Destruye autom\xE1ticamente los secretos despu\xE9s de ser accedidos para m\xE1xima seguridad"
      },
      failedAttempts: {
        title: "Destruir Despu\xE9s de Intentos Fallidos",
        description: "Destruye autom\xE1ticamente los secretos despu\xE9s de varios intentos fallidos de acceso para mayor seguridad"
      },
      expiration: {
        title: "Auto-Expiraci\xF3n",
        description: "Establece tiempos de expiraci\xF3n personalizados para asegurar que los secretos no persistan m\xE1s de lo necesario"
      },
      password: {
        title: "Protecci\xF3n con Contrase\xF1a",
        description: "A\xF1ade una capa extra de seguridad con protecci\xF3n opcional por contrase\xF1a"
      },
      files: {
        title: "Compartir Archivos",
        description: "Comparte archivos de forma segura con funcionalidad simple de arrastrar y soltar"
      },
      webhooks: {
        title: "Webhooks",
        description: "Recibe notificaciones cuando tus secretos son accedidos, destruidos o fallan al ser le\xEDdos"
      },
      ipControl: {
        title: "Restricciones IP",
        description: "Controla el acceso limitando qu\xE9 direcciones IP o rangos CIDR pueden ver tus secretos"
      },
      readLimits: {
        title: "L\xEDmites de Lectura",
        description: "Establece conteos m\xE1ximos de vistas para restringir cu\xE1ntas veces se puede acceder a un secreto"
      },
      qrCode: {
        title: "C\xF3digos QR",
        description: "Genera c\xF3digos QR para compartir f\xE1cilmente tus URLs secretas en m\xF3viles"
      },
      cli: {
        title: "Herramienta CLI",
        description: "Automatiza el compartir secretos con nuestra interfaz de l\xEDnea de comandos"
      },
      chromeExtension: {
        title: "Extensi\xF3n de Chrome",
        description: "Comparte secretos directamente desde tu navegador con nuestra extensi\xF3n de Chrome"
      },
      docker: {
        title: "Soporte Docker",
        description: "Despliega tu propia instancia usando nuestras im\xE1genes Docker oficiales"
      }
    },
    steps: {
      encrypt: {
        title: "1. Cifrar",
        description: "Tu secreto se cifra en tu navegador antes de ser almacenado"
      },
      share: {
        title: "2. Compartir",
        description: "Env\xEDa el enlace seguro a tu destinatario"
      },
      autoDelete: {
        title: "3. Auto-Eliminaci\xF3n",
        description: "El secreto se elimina permanentemente despu\xE9s de ser visto"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Contenido secreto",
        placeholder: "Ingresa tu contenido secreto aqu\xED...",
        fileHint: "agrega un archivo arrastr\xE1ndolo o haciendo clic aqu\xED",
        fileSelected: "Archivo seleccionado: {{name}} ({{size}} KB)"
      },
      password: {
        label: "Contrase\xF1a",
        placeholder: "Opcional (pero recomendado)"
      },
      ttl: {
        label: "Tiempo de vida",
        placeholder: "Selecciona el tiempo de expiraci\xF3n"
      },
      burn: {
        label: "Destruir despu\xE9s de leer",
        description: "Garantiza que solo un destinatario pueda acceder al secreto"
      },
      advanced: {
        toggle: "configuraci\xF3n avanzada",
        ip: {
          label: "Lista de permitidos IP/CIDR",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restringir el acceso a direcciones IP espec\xEDficas o bloques CIDR (separados por comas)"
        },
        readCount: {
          label: "Contador de lecturas",
          description: "N\xFAmero m\xE1ximo de veces que se puede leer el secreto"
        },
        failedAttempts: {
          label: "Eliminar despu\xE9s de intentos fallidos",
          description: "Eliminar autom\xE1ticamente el secreto despu\xE9s de varios intentos fallidos de acceso"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "URL del webhook a llamar cuando el secreto es le\xEDdo, destruido o falla al leer (configurable, si se establece)",
          read: "Lectura",
          burn: "Destrucci\xF3n",
          failureToReadPK: "Fallo (contrase\xF1a o clave)",
          failureToReadIP: "Fallo (IP o CIDR)",
          nameLabel: "Nombre",
          namePlaceholder: "Ingrese un nombre para este webhook",
          nameDescription: "Un nombre para identificar este webhook en las notificaciones"
        }
      }
    },
    success: {
      title: "\xA1Secreto Creado!",
      description: {
        main: "Tu secreto ha sido creado y la URL ha sido copiada al portapapeles",
        password: "Comparte la URL y la contrase\xF1a con el destinatario deseado"
      },
      urlCopied: "URL copiada al portapapeles",
      qrCode: {
        title: "C\xF3digo QR de la URL del Secreto",
        description: "Descarga y comparte el c\xF3digo QR de la URL del secreto"
      },
      createAnother: "Crear Otro",
      deleteSecret: "Eliminar Secreto",
      info: {
        expires: "Expira en: {{time}}",
        burn: "El secreto se eliminar\xE1 despu\xE9s de ser visto",
        passwordProtected: "Protegido con contrase\xF1a",
        ipRestrictions: "Restricci\xF3n(es) de IP: {{ips}}",
        readCount: "N\xFAmero de lecturas: {{count}}",
        webhook: "Webhook configurado para: {{events}} ({{url}})",
        failureCount: "El secreto se eliminar\xE1 despu\xE9s de {{count}} intentos fallidos"
      }
    },
    errors: {
      contentRequired: "El contenido es requerido",
      readCountWithBurn: "El contador de lecturas no se puede usar con destruir despu\xE9s de leer",
      tooManyIps: "Demasiadas restricciones de IP (m\xE1ximo {{max}})",
      invalidIp: "Direcci\xF3n IP o bloque CIDR inv\xE1lido: {{ip}}",
      uploadFailed: "Error al procesar el contenido cargado: {{error}}",
      deleteFailed: "Error al eliminar el secreto: {{error}}",
      secretNotFound: "secreto no encontrado",
      unexpectedStatus: "c\xF3digo de estado inesperado {{code}}",
      webhookConfigInvalid: "La configuraci\xF3n del webhook no es v\xE1lida: se requiere al menos un tipo de evento de webhook",
      fileSizeExceeded: "El tama\xF1o del archivo excede el tama\xF1o m\xE1ximo permitido",
      fileReadError: "Error al leer el archivo",
      fileReadAborted: "La lectura del archivo fue interrumpida"
    }
  },
  view: {
    notFound: {
      title: "Secreto No Encontrado",
      description: "Este secreto puede haber expirado o sido eliminado.",
      createNew: "Crear Nuevo Secreto"
    },
    password: {
      title: "Ingresar Contrase\xF1a",
      placeholder: "Ingresa la contrase\xF1a",
      description: "Este secreto est\xE1 protegido con una contrase\xF1a - solic\xEDtala al remitente",
      error: "Contrase\xF1a incorrecta"
    },
    content: {
      fileShared: "Se ha compartido un archivo contigo",
      downloadFile: "Descargar Archivo",
      hideContent: "Ocultar contenido",
      showContent: "Mostrar contenido",
      copyToClipboard: "Copiar al portapapeles",
      copiedToClipboard: "Secreto copiado al portapapeles",
      clickToReveal: "Haz clic en el \xEDcono del ojo arriba para revelar el secreto",
      passwordProtected: "Este secreto est\xE1 protegido con contrase\xF1a. Haz clic para ingresar la contrase\xF1a.",
      clickToEnterPassword: "Haz clic para ingresar la contrase\xF1a"
    },
    info: {
      burnedAfterReading: "Este secreto fue eliminado despu\xE9s de tu visualizaci\xF3n y ya no estar\xE1 disponible al salir de la p\xE1gina.",
      expiresIn: "Expira {{time}}"
    },
    errors: {
      notFound: "no encontrado",
      unexpectedStatus: "c\xF3digo de estado inesperado {{code}}"
    },
    actions: {
      viewSecret: "Ver Secreto"
    }
  },
  about: {
    title: "Acerca de",
    intro: "crypt.fyi es una plataforma segura, de c\xF3digo abierto y ef\xEDmera para compartir datos que te permite compartir informaci\xF3n sensible de manera segura. Ya sean contrase\xF1as, claves API o mensajes confidenciales, crypt.fyi asegura que tus datos permanezcan privados y desaparezcan autom\xE1ticamente despu\xE9s de ser accedidos.",
    whyCryptFyi: {
      title: "\xBFPor qu\xE9 crypt.fyi?",
      commonPractices: {
        title: "El Problema con las Pr\xE1cticas Comunes",
        description: "Cada d\xEDa, informaci\xF3n sensible como contrase\xF1as, claves API y datos privados se comparte a trav\xE9s de canales inseguros en texto plano:",
        problems: {
          email: "Correo electr\xF3nico - puede ser interceptado, almacenado indefinidamente y reenviado sin control",
          slack: "Mensajes de Slack/Teams - permanecen en el historial del chat y registros de la empresa",
          sms: "SMS/Mensajes de texto - almacenados en m\xFAltiples dispositivos y servidores de operadores",
          messaging: "Mensajer\xEDa instant\xE1nea - a menudo carece de cifrado adecuado y eliminaci\xF3n de datos"
        }
      },
      existingSolutions: {
        title: "Soluciones Existentes y Sus Limitaciones",
        description: "Si bien hay otras herramientas en este espacio, cada una tiene sus limitaciones:",
        limitations: {
          onePassword: "1Password - excelente para la gesti\xF3n de contrase\xF1as en equipo, pero",
          onePasswordLink: "no admite compartir externamente con usuarios no registrados",
          otherTools: "PrivateBin/PwPush/OneTimeSecret - funcionalidad b\xE1sica similar, pero interfaces de usuario desactualizadas y pilas tecnol\xF3gicas antiguas, adem\xE1s de algunas",
          otherToolsConfigLink: "configuraciones",
          otherToolsSecurityLink: "caracter\xEDsticas de seguridad"
        }
      },
      approach: {
        title: "El Enfoque de crypt.fyi",
        description: "crypt.fyi fue construido para abordar estos desaf\xEDos mientras adopta tecnolog\xEDas web modernas. Combina los principios de seguridad de las soluciones existentes con una interfaz limpia e intuitiva y una pila tecnol\xF3gica moderna. El resultado es una herramienta que es tanto altamente segura como agradable de usar."
      }
    },
    howItWorks: {
      title: "C\xF3mo Funciona",
      steps: {
        encrypt: {
          title: "1. Cifrar",
          description: "Tu secreto se cifra directamente en tu navegador antes de salir de tu dispositivo. Solo las personas con el enlace especial, que has compartido expl\xEDcitamente, pueden descifrarlo."
        },
        share: {
          title: "2. Compartir",
          description: "Comparte el enlace seguro con tu destinatario. El enlace contiene todo lo necesario para descifrar el mensaje, a menos que se especifique una contrase\xF1a."
        },
        burn: {
          title: "3. Destruir despu\xE9s de leer",
          description: 'Una vez accedido, si "destruir despu\xE9s de leer" est\xE1 marcado, el secreto se elimina permanentemente de nuestros servidores. Sin dejar rastros.'
        }
      }
    },
    security: {
      title: "Implementaci\xF3n de Seguridad",
      encryption: {
        title: "Cifrado de Extremo a Extremo",
        description: "Todos los secretos se cifran usando cifrado AES-256-GCM en tu navegador antes de la transmisi\xF3n. La clave de cifrado nunca sale de tu dispositivo, asegurando un verdadero cifrado de extremo a extremo.",
        features: {
          key: "La clave de cifrado se deriva de una generaci\xF3n aleatoria criptogr\xE1ficamente segura",
          derivation: "La derivaci\xF3n de claves usa PBKDF2 con SHA-256",
          vector: "Cada secreto tiene un vector de inicializaci\xF3n (IV) \xFAnico"
        }
      },
      zeroKnowledge: {
        title: "Arquitectura de Conocimiento Cero",
        description: "Nuestros servidores nunca ven tus datos sin cifrar. Empleamos una arquitectura de conocimiento cero donde:",
        features: {
          clientSide: "Todo el cifrado/descifrado ocurre del lado del cliente",
          storage: "Los servidores solo almacenan datos cifrados",
          keys: "Las claves de cifrado se transmiten a trav\xE9s de fragmentos de URL, que nunca llegan al servidor API backend"
        }
      },
      protection: {
        title: "Protecci\xF3n de Datos",
        description: "M\xFAltiples capas de seguridad aseguran que tus datos permanezcan protegidos:",
        features: {
          encryption: "Cifrado/descifrado del lado del cliente",
          tls: "Cifrado TLS para todas las comunicaciones API",
          destruction: "Destrucci\xF3n autom\xE1tica del secreto despu\xE9s del acceso",
          logging: "Sin registro de datos sensibles en el servidor",
          password: "Protecci\xF3n opcional con contrase\xF1a para seguridad adicional"
        }
      }
    },
    openSource: {
      title: "C\xF3digo Abierto",
      description: "crypt.fyi es de c\xF3digo abierto y auditable. Puedes revisar nuestro c\xF3digo, enviar problemas y contribuir en"
    }
  },
  privacy: {
    title: "Pol\xEDtica de Privacidad",
    intro: "En crypt.fyi, nos tomamos en serio tu privacidad. Esta Pol\xEDtica de Privacidad explica c\xF3mo manejamos tu informaci\xF3n cuando utilizas nuestra plataforma de intercambio de secretos con conocimiento cero y cifrado de extremo a extremo.",
    doNotCollect: {
      title: "Informaci\xF3n que No Recopilamos",
      description: "Debido a nuestra arquitectura de conocimiento cero, somos t\xE9cnicamente incapaces de acceder a:",
      items: {
        secrets: "Tus secretos o archivos sin cifrar",
        keys: "Claves de cifrado o contrase\xF1as",
        urls: "Fragmentos de URL que contienen informaci\xF3n de descifrado",
        content: "El contenido de tus datos cifrados",
        recipients: "Informaci\xF3n sobre los destinatarios de tus secretos"
      }
    },
    collect: {
      title: "Informaci\xF3n que Recopilamos",
      description: "Solo recopilamos y almacenamos la informaci\xF3n m\xEDnima necesaria:",
      items: {
        encrypted: "Datos cifrados (que no podemos descifrar)",
        hashes: "Hashes de verificaci\xF3n de claves (utilizados para verificar derechos de acceso sin conocer las claves reales)",
        metadata: "Metadatos b\xE1sicos de solicitudes (direcciones IP, marcas de tiempo) para l\xEDmites de tasa y prevenci\xF3n de abusos",
        webhooks: "URLs de webhook si se proporcionan (para notificaciones de acceso y eliminaci\xF3n de secretos)"
      },
      note: "Todos los datos almacenados se eliminan autom\xE1ticamente despu\xE9s de su vencimiento o al acceder (si est\xE1 activada la destrucci\xF3n despu\xE9s de lectura)."
    },
    usage: {
      title: "C\xF3mo Utilizamos la Informaci\xF3n",
      description: "Utilizamos la informaci\xF3n recopilada \xFAnicamente para:",
      items: {
        transmission: "Facilitar la transmisi\xF3n segura de tus secretos cifrados",
        rateLimits: "Aplicar l\xEDmites de tasa para prevenir abusos",
        ipControl: "Implementar lista blanca de IP/CIDR cuando est\xE1 configurada",
        notifications: "Enviar notificaciones webhook cuando est\xE1n activadas",
        security: "Mantener la seguridad del sistema y prevenir accesos no autorizados"
      }
    },
    security: {
      title: "Almacenamiento y Seguridad de Datos",
      description: "Nuestras medidas de seguridad incluyen:",
      items: {
        encryption: "Todo el cifrado/descifrado ocurre en tu navegador usando AES-256-GCM",
        csp: "Pol\xEDtica de Seguridad de Contenido (CSP) estricta para prevenir ataques XSS",
        tls: "Cifrado TLS para todas las comunicaciones API",
        expiration: "Vencimiento autom\xE1tico de datos con tiempo de vida configurable (TTL)",
        deletion: "Eliminaci\xF3n segura de datos despu\xE9s del acceso o vencimiento",
        storage: "Sin almacenamiento persistente de informaci\xF3n sensible"
      }
    },
    thirdParty: {
      title: "Servicios de Terceros",
      description: "No compartimos informaci\xF3n con terceros excepto en los siguientes casos:",
      items: {
        webhooks: "Cuando activas los webhooks, enviamos notificaciones a las URLs que proporcionas sobre el acceso y eliminaci\xF3n de secretos",
        infrastructure: "Proveedores de infraestructura que alojan nuestros servicios (que solo ven datos cifrados que no pueden descifrar)"
      }
    },
    rights: {
      title: "Tus Derechos y Opciones",
      description: "Tienes control sobre tus datos:",
      items: {
        expiration: "Elegir tiempos de vencimiento personalizados para tus secretos",
        burn: "Activar destrucci\xF3n despu\xE9s de lectura para eliminaci\xF3n inmediata despu\xE9s del acceso",
        password: "Agregar protecci\xF3n con contrase\xF1a para seguridad adicional",
        ip: "Configurar restricciones de IP para controlar el acceso",
        readLimits: "Establecer l\xEDmites de lectura"
      }
    },
    changes: {
      title: "Cambios en esta Pol\xEDtica",
      description: "Podemos actualizar esta Pol\xEDtica de Privacidad de vez en cuando. Informaremos a los usuarios sobre cambios significativos publicando la nueva Pol\xEDtica de Privacidad en esta p\xE1gina. Te recomendamos revisar peri\xF3dicamente esta Pol\xEDtica de Privacidad para cualquier cambio."
    },
    contact: {
      title: "Cont\xE1ctanos",
      description: "Si tienes preguntas sobre esta Pol\xEDtica de Privacidad, puedes contactarnos a trav\xE9s de nuestra"
    }
  }
};

// src/i18n/locales/de.ts
var de = {
  common: {
    createSecret: "Geheimnis erstellen",
    starOnGithub: "Auf GitHub markieren",
    features: "Funktionen",
    learnMore: "Mehr erfahren",
    loading: "L\xE4dt...",
    create: "Erstellen",
    delete: "L\xF6schen",
    copy: "Kopieren",
    share: "Teilen",
    download: "Herunterladen",
    success: "Erfolg!",
    error: "Fehler",
    confirm: "Best\xE4tigen",
    requestNewLanguage: "Neue Sprache anfordern oder \xDCbersetzungen korrigieren",
    footer: {
      tagline: "Mit Sicherheit und Privatsph\xE4re im Fokus - weil Unwissenheit ein Segen sein kann"
    },
    header: {
      tagline: "Kurzlebiger Datenaustausch mit Zero-Knowledge <aesLink>AES-256</aesLink> <e2eLink>Ende-zu-Ende-Verschl\xFCsselung</e2eLink>"
    },
    time: {
      minute_one: "{{count}} Minute",
      minute_other: "{{count}} Minuten",
      hour_one: "{{count}} Stunde",
      hour_other: "{{count}} Stunden",
      day_one: "{{count}} Tag",
      day_other: "{{count}} Tage"
    }
  },
  landing: {
    title: "Sicherer Austausch sensibler Daten",
    subtitle: "crypt.fyi erm\xF6glicht Zero-Knowledge Ende-zu-Ende verschl\xFCsseltes Teilen sensibler Daten mit automatischer L\xF6schung",
    features: {
      encryption: {
        title: "Ende-zu-Ende-Verschl\xFCsselung",
        description: "Sichern Sie Ihre Daten mit AES-256-GCM-Verschl\xFCsselung, bevor sie Ihren Browser verlassen"
      },
      security: {
        title: "Erweiterte Sicherheit",
        description: "Strikte Content Security Policy (CSP) und Ratenbegrenzungen zum Schutz vor XSS- und Brute-Force-Angriffen"
      },
      zeroKnowledge: {
        title: "Zero-Knowledge",
        description: "Unsere Server sehen nie Ihre unverschl\xFCsselten Daten - nur Sie und Ihr Empf\xE4nger haben Zugriff"
      },
      burn: {
        title: "Einmal-Lesen",
        description: "Geheimnisse werden nach dem Lesen automatisch gel\xF6scht f\xFCr maximale Sicherheit"
      },
      failedAttempts: {
        title: "L\xF6schen nach fehlgeschlagenen Versuchen",
        description: "Geheimnisse werden nach einer Anzahl fehlgeschlagener Zugriffsversuche automatisch gel\xF6scht f\xFCr erh\xF6hte Sicherheit"
      },
      expiration: {
        title: "Auto-Ablauf",
        description: "Legen Sie benutzerdefinierte Ablaufzeiten fest, damit Geheimnisse nicht l\xE4nger als n\xF6tig bestehen"
      },
      password: {
        title: "Passwortschutz",
        description: "F\xFCgen Sie eine zus\xE4tzliche Sicherheitsebene mit optionalem Passwortschutz hinzu"
      },
      files: {
        title: "Datei-Sharing",
        description: "Teilen Sie Dateien sicher mit einfacher Drag-and-Drop-Funktionalit\xE4t"
      },
      webhooks: {
        title: "Webhooks",
        description: "Erhalten Sie Benachrichtigungen, wenn Ihre Geheimnisse gelesen, gel\xF6scht oder nicht gelesen werden k\xF6nnen"
      },
      ipControl: {
        title: "IP-Beschr\xE4nkungen",
        description: "Steuern Sie den Zugriff durch Einschr\xE4nkung der IP-Adressen oder CIDR-Bereiche"
      },
      readLimits: {
        title: "Lesebeschr\xE4nkungen",
        description: "Legen Sie maximale Ansichtszahlen fest, um zu begrenzen, wie oft ein Geheimnis abgerufen werden kann"
      },
      qrCode: {
        title: "QR-Codes",
        description: "Generieren Sie QR-Codes f\xFCr einfaches Teilen Ihrer geheimen URLs auf Mobilger\xE4ten"
      },
      cli: {
        title: "CLI-Tool",
        description: "Automatisieren Sie das Teilen von Geheimnissen mit unserer Kommandozeilen-Schnittstelle"
      },
      chromeExtension: {
        title: "Chrome-Erweiterung",
        description: "Teilen Sie Geheimnisse direkt aus Ihrem Browser mit unserer Chrome-Erweiterung"
      },
      docker: {
        title: "Docker-Support",
        description: "Stellen Sie Ihre eigene Instanz mit unseren offiziellen Docker-Images bereit"
      }
    },
    steps: {
      encrypt: {
        title: "1. Verschl\xFCsseln",
        description: "Ihr Geheimnis wird in Ihrem Browser verschl\xFCsselt, bevor es gespeichert wird"
      },
      share: {
        title: "2. Teilen",
        description: "Senden Sie den sicheren Link an Ihren gew\xFCnschten Empf\xE4nger"
      },
      autoDelete: {
        title: "3. Automatisch l\xF6schen",
        description: "Das Geheimnis wird nach dem Anzeigen permanent gel\xF6scht"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Geheimer Inhalt",
        placeholder: "Geben Sie hier Ihren geheimen Inhalt ein...",
        fileHint: "Datei per Drag & Drop hinzuf\xFCgen oder hier klicken",
        fileSelected: "Datei ausgew\xE4hlt: {{name}} ({{size}} KB)"
      },
      password: {
        label: "Passwort",
        placeholder: "Optional (aber empfohlen)"
      },
      ttl: {
        label: "G\xFCltigkeitsdauer",
        placeholder: "Ablaufzeit ausw\xE4hlen"
      },
      burn: {
        label: "Nach dem Lesen l\xF6schen",
        description: "Garantiert, dass nur ein Empf\xE4nger auf das Geheimnis zugreifen kann"
      },
      advanced: {
        toggle: "erweiterte Konfiguration",
        ip: {
          label: "IP/CIDR Positivliste",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Zugriff auf bestimmte IP-Adressen oder CIDR-Bl\xF6cke beschr\xE4nken (durch Kommas getrennt)"
        },
        readCount: {
          label: "Lesez\xE4hler",
          description: "Maximale Anzahl der Abrufe des Geheimnisses"
        },
        failedAttempts: {
          label: "Nach fehlgeschlagenen Versuchen l\xF6schen",
          description: "Geheimnis automatisch nach einer Anzahl fehlgeschlagener Zugriffsversuche l\xF6schen"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "Webhook-URL, die aufgerufen wird, wenn das Geheimnis gelesen, gel\xF6scht oder nicht gelesen werden kann (konfigurierbar, falls gesetzt)",
          read: "Lesen",
          burn: "L\xF6schen",
          failureToReadPK: "Fehler (Passwort oder Schl\xFCssel)",
          failureToReadIP: "Fehler (IP oder CIDR)",
          nameLabel: "Name",
          namePlaceholder: "Geben Sie einen Namen f\xFCr diesen Webhook ein",
          nameDescription: "Ein Name zur Identifizierung dieses Webhooks in Benachrichtigungen"
        }
      }
    },
    success: {
      title: "Geheimnis erstellt!",
      description: {
        main: "Ihr Geheimnis wurde erstellt und die URL wurde in die Zwischenablage kopiert",
        password: "Teilen Sie die URL und das Passwort mit dem gew\xFCnschten Empf\xE4nger"
      },
      urlCopied: "URL in die Zwischenablage kopiert",
      qrCode: {
        title: "Geheimnis-URL QR-Code",
        description: "Laden Sie den QR-Code der Geheimnis-URL herunter und teilen Sie ihn"
      },
      createAnother: "Weiteres erstellen",
      deleteSecret: "Geheimnis l\xF6schen",
      info: {
        expires: "L\xE4uft ab in: {{time}}",
        burn: "Geheimnis wird nach dem Lesen gel\xF6scht",
        passwordProtected: "Passwortgesch\xFCtzt",
        ipRestrictions: "IP-Beschr\xE4nkung(en): {{ips}}",
        readCount: "Leseanzahl: {{count}}",
        webhook: "Webhook konfiguriert f\xFCr: {{events}} ({{url}})",
        failureCount: "Geheimnis wird nach {{count}} fehlgeschlagenen Versuchen gel\xF6scht"
      }
    },
    errors: {
      contentRequired: "Inhalt ist erforderlich",
      readCountWithBurn: 'Lesez\xE4hler kann nicht mit "Nach dem Lesen l\xF6schen" kombiniert werden',
      tooManyIps: "Zu viele IP-Beschr\xE4nkungen (maximal {{max}})",
      invalidIp: "Ung\xFCltige IP-Adresse oder CIDR-Block: {{ip}}",
      uploadFailed: "Verarbeitung des Inhalts fehlgeschlagen: {{error}}",
      deleteFailed: "L\xF6schen des Geheimnisses fehlgeschlagen: {{error}}",
      secretNotFound: "Geheimnis nicht gefunden",
      unexpectedStatus: "Unerwarteter Statuscode {{code}}",
      webhookConfigInvalid: "Webhook-Konfiguration ist ung\xFCltig - mindestens ein Webhook-Ereignistyp ist erforderlich",
      fileSizeExceeded: "Dateigr\xF6\xDFe \xFCberschreitet die maximal zul\xE4ssige Gr\xF6\xDFe",
      fileReadError: "Fehler beim Lesen der Datei",
      fileReadAborted: "Dateilesen wurde abgebrochen"
    }
  },
  view: {
    notFound: {
      title: "Geheimnis nicht gefunden",
      description: "Dieses Geheimnis ist m\xF6glicherweise abgelaufen oder wurde gel\xF6scht.",
      createNew: "Neues Geheimnis erstellen"
    },
    password: {
      title: "Passwort eingeben",
      placeholder: "Geben Sie das Passwort ein",
      description: "Dieses Geheimnis ist mit einem Passwort gesch\xFCtzt - fragen Sie den Absender",
      error: "Falsches Passwort"
    },
    content: {
      fileShared: "Eine Datei wurde mit Ihnen geteilt",
      downloadFile: "Datei herunterladen",
      hideContent: "Inhalt verbergen",
      showContent: "Inhalt anzeigen",
      copyToClipboard: "In die Zwischenablage kopieren",
      copiedToClipboard: "Geheimnis in die Zwischenablage kopiert",
      clickToReveal: "Klicken Sie auf das Augensymbol oben, um das Geheimnis anzuzeigen",
      passwordProtected: "Dieses Geheimnis ist passwortgesch\xFCtzt. Klicken Sie, um das Passwort einzugeben.",
      clickToEnterPassword: "Klicken Sie, um das Passwort einzugeben"
    },
    info: {
      burnedAfterReading: "Dieses Geheimnis wurde nach Ihrer Ansicht gel\xF6scht und ist nach Verlassen der Seite nicht mehr verf\xFCgbar.",
      expiresIn: "L\xE4uft ab in {{time}}"
    },
    errors: {
      notFound: "nicht gefunden",
      unexpectedStatus: "unerwarteter Statuscode {{code}}"
    },
    actions: {
      viewSecret: "Geheimnis anzeigen"
    }
  },
  about: {
    title: "\xDCber uns",
    intro: "crypt.fyi ist eine sichere, quelloffene Plattform f\xFCr kurzlebigen Datenaustausch, die es Ihnen erm\xF6glicht, sensible Informationen sicher zu teilen. Ob Passw\xF6rter, API-Schl\xFCssel oder vertrauliche Nachrichten - crypt.fyi stellt sicher, dass Ihre Daten privat bleiben und nach dem Zugriff automatisch verschwinden.",
    whyCryptFyi: {
      title: "Warum crypt.fyi?",
      commonPractices: {
        title: "Das Problem mit g\xE4ngigen Praktiken",
        description: "T\xE4glich werden sensible Informationen wie Passw\xF6rter, API-Schl\xFCssel und private Daten \xFCber unsichere Kan\xE4le im Klartext geteilt:",
        problems: {
          email: "E-Mail - kann abgefangen, unbegrenzt gespeichert und unkontrolliert weitergeleitet werden",
          slack: "Slack/Teams-Nachrichten - bleiben im Chat-Verlauf und Firmenprotokollen",
          sms: "SMS/Textnachrichten - auf mehreren Ger\xE4ten und Betreiberservern gespeichert",
          messaging: "Instant Messaging - oft ohne angemessene Verschl\xFCsselung und Datenl\xF6schung"
        }
      },
      existingSolutions: {
        title: "Bestehende L\xF6sungen und ihre Grenzen",
        description: "W\xE4hrend es andere Tools in diesem Bereich gibt, hat jedes seine Einschr\xE4nkungen:",
        limitations: {
          onePassword: "1Password - hervorragend f\xFCr Team-Passwortverwaltung, aber",
          onePasswordLink: "unterst\xFCtzt keine externe Freigabe f\xFCr Nicht-Benutzer",
          otherTools: "PrivateBin/PwPush/OneTimeSecret - \xE4hnliche Kernfunktionalit\xE4t, aber veraltete Benutzeroberfl\xE4chen und Technologie-Stacks sowie fehlende",
          otherToolsConfigLink: "Konfigurierbarkeit",
          otherToolsSecurityLink: "Sicherheitsfunktionen"
        }
      },
      approach: {
        title: "Der crypt.fyi-Ansatz",
        description: "crypt.fyi wurde entwickelt, um diese Herausforderungen zu bew\xE4ltigen und dabei moderne Webtechnologien zu nutzen. Es kombiniert die Sicherheitsprinzipien bestehender L\xF6sungen mit einer \xFCbersichtlichen, intuitiven Oberfl\xE4che und einem modernen Tech-Stack. Das Ergebnis ist ein Tool, das sowohl hochsicher als auch angenehm zu benutzen ist."
      }
    },
    howItWorks: {
      title: "Wie es funktioniert",
      steps: {
        encrypt: {
          title: "1. Verschl\xFCsseln",
          description: "Ihr Geheimnis wird direkt in Ihrem Browser verschl\xFCsselt, bevor es Ihr Ger\xE4t verl\xE4sst. Nur Personen mit dem speziellen Link, den Sie explizit geteilt haben, k\xF6nnen es entschl\xFCsseln."
        },
        share: {
          title: "2. Teilen",
          description: "Teilen Sie den sicheren Link mit Ihrem gew\xFCnschten Empf\xE4nger. Der Link enth\xE4lt alles Notwendige zur Entschl\xFCsselung der Nachricht, sofern kein Passwort festgelegt wurde."
        },
        burn: {
          title: "3. Nach dem Lesen l\xF6schen",
          description: 'Nach dem Zugriff wird das Geheimnis, falls "Nach dem Lesen l\xF6schen" aktiviert ist, permanent von unseren Servern gel\xF6scht. Keine Spuren bleiben zur\xFCck.'
        }
      }
    },
    security: {
      title: "Sicherheitsimplementierung",
      encryption: {
        title: "Ende-zu-Ende-Verschl\xFCsselung",
        description: "Alle Geheimnisse werden vor der \xDCbertragung in Ihrem Browser mit AES-256-GCM-Verschl\xFCsselung verschl\xFCsselt. Der Verschl\xFCsselungsschl\xFCssel verl\xE4sst nie Ihr Ger\xE4t und gew\xE4hrleistet so echte Ende-zu-Ende-Verschl\xFCsselung.",
        features: {
          key: "Verschl\xFCsselungsschl\xFCssel wird aus einer kryptografisch sicheren Zufallsgenerierung abgeleitet",
          derivation: "Schl\xFCsselableitung verwendet PBKDF2 mit SHA-256",
          vector: "Jedes Geheimnis hat einen einzigartigen Initialisierungsvektor (IV)"
        }
      },
      zeroKnowledge: {
        title: "Zero-Knowledge-Architektur",
        description: "Unsere Server sehen nie Ihre unverschl\xFCsselten Daten. Wir verwenden eine Zero-Knowledge-Architektur, bei der:",
        features: {
          clientSide: "Alle Ver- und Entschl\xFCsselung clientseitig erfolgt",
          storage: "Server nur verschl\xFCsselte Daten speichern",
          keys: "Verschl\xFCsselungsschl\xFCssel \xFCber URL-Fragmente \xFCbertragen werden, die nie den Backend-API-Server erreichen"
        }
      },
      protection: {
        title: "Datenschutz",
        description: "Mehrere Sicherheitsebenen stellen sicher, dass Ihre Daten gesch\xFCtzt bleiben:",
        features: {
          encryption: "Clientseitige Ver- und Entschl\xFCsselung",
          tls: "TLS-Verschl\xFCsselung f\xFCr alle API-Kommunikation",
          destruction: "Automatische Geheimniszerst\xF6rung nach Zugriff",
          logging: "Keine serverseitige Protokollierung sensibler Daten",
          password: "Optionaler Passwortschutz f\xFCr zus\xE4tzliche Sicherheit"
        }
      }
    },
    openSource: {
      title: "Open Source",
      description: "crypt.fyi ist quelloffen und \xFCberpr\xFCfbar. Sie k\xF6nnen unseren Code einsehen, Probleme melden und auf GitHub beitragen"
    }
  },
  privacy: {
    title: "Datenschutzerkl\xE4rung",
    intro: "Bei crypt.fyi nehmen wir Ihre Privatsph\xE4re ernst. Diese Datenschutzerkl\xE4rung erl\xE4utert, wie wir mit Ihren Informationen umgehen, wenn Sie unsere Zero-Knowledge, Ende-zu-Ende-verschl\xFCsselte Plattform f\xFCr den Austausch von Geheimnissen nutzen.",
    doNotCollect: {
      title: "Informationen, die wir nicht sammeln",
      description: "Aufgrund unserer Zero-Knowledge-Architektur sind wir technisch nicht in der Lage, auf Folgendes zuzugreifen:",
      items: {
        secrets: "Ihre unverschl\xFCsselten Geheimnisse oder Dateien",
        keys: "Verschl\xFCsselungsschl\xFCssel oder Passw\xF6rter",
        urls: "URL-Fragmente mit Entschl\xFCsselungsinformationen",
        content: "Den Inhalt Ihrer verschl\xFCsselten Daten",
        recipients: "Informationen \xFCber die Empf\xE4nger Ihrer Geheimnisse"
      }
    },
    collect: {
      title: "Informationen, die wir sammeln",
      description: "Wir sammeln und speichern nur die minimal erforderlichen Informationen:",
      items: {
        encrypted: "Verschl\xFCsselte Daten (die wir nicht entschl\xFCsseln k\xF6nnen)",
        hashes: "Schl\xFCssel-Verifizierungs-Hashes (verwendet zur \xDCberpr\xFCfung der Zugriffsrechte ohne die tats\xE4chlichen Schl\xFCssel zu kennen)",
        metadata: "Grundlegende Anfrage-Metadaten (IP-Adressen, Zeitstempel) f\xFCr Ratenbegrenzung und Missbrauchspr\xE4vention",
        webhooks: "Webhook-URLs, falls angegeben (f\xFCr Benachrichtigungen \xFCber Zugriff und L\xF6schung von Geheimnissen)"
      },
      note: 'Alle gespeicherten Daten werden nach Ablauf oder beim Zugriff automatisch gel\xF6scht (wenn "Nach dem Lesen l\xF6schen" aktiviert ist).'
    },
    usage: {
      title: "Wie wir Informationen verwenden",
      description: "Wir verwenden die gesammelten Informationen nur f\xFCr:",
      items: {
        transmission: "Erm\xF6glichung der sicheren \xDCbertragung Ihrer verschl\xFCsselten Geheimnisse",
        rateLimits: "Durchsetzung von Ratenbegrenzungen zur Verhinderung von Missbrauch",
        ipControl: "Implementierung von IP/CIDR-Whitelisting, wenn konfiguriert",
        notifications: "Senden von Webhook-Benachrichtigungen, wenn aktiviert",
        security: "Aufrechterhaltung der Systemsicherheit und Verhinderung unbefugter Zugriffe"
      }
    },
    security: {
      title: "Datenspeicherung und Sicherheit",
      description: "Unsere Sicherheitsma\xDFnahmen umfassen:",
      items: {
        encryption: "Alle Ver- und Entschl\xFCsselung erfolgt in Ihrem Browser unter Verwendung von AES-256-GCM",
        csp: "Strikte Content Security Policy (CSP) zur Verhinderung von XSS-Angriffen",
        tls: "TLS-Verschl\xFCsselung f\xFCr alle API-Kommunikation",
        expiration: "Automatischer Datenablauf mit konfigurierbarer Lebensdauer (TTL)",
        deletion: "Sichere Datenl\xF6schung nach Zugriff oder Ablauf",
        storage: "Keine dauerhafte Speicherung sensibler Informationen"
      }
    },
    thirdParty: {
      title: "Drittanbieterdienste",
      description: "Wir teilen keine Informationen mit Dritten, au\xDFer in den folgenden F\xE4llen:",
      items: {
        webhooks: "Wenn Sie Webhooks aktivieren, senden wir Benachrichtigungen an die von Ihnen angegebenen URLs \xFCber Zugriff und L\xF6schung von Geheimnissen",
        infrastructure: "Infrastrukturanbieter, die unsere Dienste hosten (die nur verschl\xFCsselte Daten sehen, die sie nicht entschl\xFCsseln k\xF6nnen)"
      }
    },
    rights: {
      title: "Ihre Rechte und Wahlm\xF6glichkeiten",
      description: "Sie haben die Kontrolle \xFCber Ihre Daten:",
      items: {
        expiration: "W\xE4hlen Sie benutzerdefinierte Ablauffristen f\xFCr Ihre Geheimnisse",
        burn: 'Aktivieren Sie "Nach dem Lesen l\xF6schen" f\xFCr sofortige L\xF6schung nach dem Zugriff',
        password: "F\xFCgen Sie Passwortschutz f\xFCr zus\xE4tzliche Sicherheit hinzu",
        ip: "Konfigurieren Sie IP-Beschr\xE4nkungen zur Zugriffskontrolle",
        readLimits: "Legen Sie Lesebeschr\xE4nkungen fest"
      }
    },
    changes: {
      title: "\xC4nderungen dieser Richtlinie",
      description: "Wir k\xF6nnen diese Datenschutzerkl\xE4rung von Zeit zu Zeit aktualisieren. Wir werden die Benutzer \xFCber wesentliche \xC4nderungen informieren, indem wir die neue Datenschutzerkl\xE4rung auf dieser Seite ver\xF6ffentlichen. Wir empfehlen Ihnen, diese Datenschutzerkl\xE4rung regelm\xE4\xDFig auf \xC4nderungen zu \xFCberpr\xFCfen."
    },
    contact: {
      title: "Kontakt",
      description: "Wenn Sie Fragen zu dieser Datenschutzerkl\xE4rung haben, k\xF6nnen Sie uns \xFCber unsere"
    }
  }
};

// src/i18n/locales/fr.ts
var fr = {
  common: {
    createSecret: "Cr\xE9er un Secret",
    starOnGithub: "Star sur GitHub",
    features: "Fonctionnalit\xE9s",
    learnMore: "En savoir plus",
    loading: "Chargement...",
    create: "Cr\xE9er",
    delete: "Supprimer",
    copy: "Copier",
    share: "Partager",
    download: "T\xE9l\xE9charger",
    success: "Succ\xE8s !",
    error: "Erreur",
    confirm: "Confirmer",
    requestNewLanguage: "Demander nouvelle langue ou corriger traductions",
    footer: {
      tagline: "Construit avec la s\xE9curit\xE9 et la confidentialit\xE9 \xE0 l'esprit - parce que l'ignorance peut \xEAtre une b\xE9n\xE9diction"
    },
    header: {
      tagline: "Partagez des donn\xE9es \xE9ph\xE9m\xE8res avec un chiffrement <aesLink>AES-256</aesLink> <e2eLink>de bout en bout</e2eLink> \xE0 connaissance nulle"
    },
    time: {
      minute_one: "{{count}} minute",
      minute_other: "{{count}} minutes",
      hour_one: "{{count}} heure",
      hour_other: "{{count}} heures",
      day_one: "{{count}} jour",
      day_other: "{{count}} jours"
    }
  },
  landing: {
    title: "Partagez des Donn\xE9es Sensibles en Toute S\xE9curit\xE9",
    subtitle: "crypt.fyi permet le partage de donn\xE9es sensibles avec chiffrement de bout en bout \xE0 connaissance nulle et suppression automatique",
    features: {
      encryption: {
        title: "Chiffrement de Bout en Bout",
        description: "S\xE9curisez vos donn\xE9es avec le chiffrement AES-256-GCM avant qu'elles ne quittent votre navigateur"
      },
      security: {
        title: "S\xE9curit\xE9 Renforc\xE9e",
        description: "Politique de s\xE9curit\xE9 du contenu (CSP) stricte et limites de d\xE9bit pour att\xE9nuer les attaques XSS et par force brute"
      },
      zeroKnowledge: {
        title: "Connaissance Nulle",
        description: "Nos serveurs ne voient jamais vos donn\xE9es non chiffr\xE9es - seuls vous et votre destinataire y ont acc\xE8s"
      },
      burn: {
        title: "Destruction Apr\xE8s Lecture",
        description: "D\xE9truisez automatiquement les secrets apr\xE8s leur consultation pour une s\xE9curit\xE9 maximale"
      },
      failedAttempts: {
        title: "Destruction Apr\xE8s Tentatives \xC9chou\xE9es",
        description: "D\xE9truit automatiquement les secrets apr\xE8s un certain nombre de tentatives d'acc\xE8s \xE9chou\xE9es pour une s\xE9curit\xE9 renforc\xE9e"
      },
      expiration: {
        title: "Auto-Expiration",
        description: "D\xE9finissez des d\xE9lais d'expiration personnalis\xE9s pour que les secrets ne persistent pas plus longtemps que n\xE9cessaire"
      },
      password: {
        title: "Protection par Mot de Passe",
        description: "Ajoutez une couche de s\xE9curit\xE9 suppl\xE9mentaire avec une protection optionnelle par mot de passe"
      },
      files: {
        title: "Partage de Fichiers",
        description: "Partagez des fichiers en toute s\xE9curit\xE9 avec une fonctionnalit\xE9 simple de glisser-d\xE9poser"
      },
      webhooks: {
        title: "Webhooks",
        description: "Soyez notifi\xE9 lorsque vos secrets sont consult\xE9s, d\xE9truits ou \xE9chouent \xE0 \xEAtre lus"
      },
      ipControl: {
        title: "Restrictions IP",
        description: "Contr\xF4lez l'acc\xE8s en limitant quelles adresses IP ou plages CIDR peuvent voir vos secrets"
      },
      readLimits: {
        title: "Limites de Lecture",
        description: "D\xE9finissez le nombre maximum de consultations pour restreindre l'acc\xE8s \xE0 vos secrets"
      },
      qrCode: {
        title: "Codes QR",
        description: "G\xE9n\xE9rez des codes QR pour un partage facile de vos URLs secr\xE8tes sur mobile"
      },
      cli: {
        title: "Outil CLI",
        description: "Automatisez le partage de secrets avec notre interface en ligne de commande"
      },
      chromeExtension: {
        title: "Extension Chrome",
        description: "Partagez des secrets directement depuis votre navigateur avec notre extension Chrome"
      },
      docker: {
        title: "Support Docker",
        description: "D\xE9ployez votre propre instance en utilisant nos images Docker officielles"
      }
    },
    steps: {
      encrypt: {
        title: "1. Chiffrer",
        description: "Votre secret est chiffr\xE9 dans votre navigateur avant d'\xEAtre stock\xE9"
      },
      share: {
        title: "2. Partager",
        description: "Envoyez le lien s\xE9curis\xE9 \xE0 votre destinataire"
      },
      autoDelete: {
        title: "3. Auto-Suppression",
        description: "Le secret est d\xE9finitivement supprim\xE9 apr\xE8s avoir \xE9t\xE9 consult\xE9"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Contenu secret",
        placeholder: "Saisissez votre contenu secret ici...",
        fileHint: "ajoutez un fichier en le d\xE9posant ou en cliquant ici",
        fileSelected: "Fichier s\xE9lectionn\xE9 : {{name}} ({{size}} Ko)"
      },
      password: {
        label: "Mot de passe",
        placeholder: "Optionnel (mais recommand\xE9)"
      },
      ttl: {
        label: "Dur\xE9e de vie",
        placeholder: "S\xE9lectionnez le d\xE9lai d'expiration"
      },
      burn: {
        label: "D\xE9truire apr\xE8s lecture",
        description: "Garantit qu'un seul destinataire peut acc\xE9der au secret"
      },
      advanced: {
        toggle: "configuration avanc\xE9e",
        ip: {
          label: "Liste d'IP/CIDR autoris\xE9es",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restreindre l'acc\xE8s \xE0 des adresses IP sp\xE9cifiques ou des blocs CIDR (s\xE9par\xE9s par des virgules)"
        },
        readCount: {
          label: "Nombre de lectures",
          description: "Nombre maximum de fois que le secret peut \xEAtre lu"
        },
        failedAttempts: {
          label: "Supprimer apr\xE8s tentatives \xE9chou\xE9es",
          description: "Supprimer automatiquement le secret apr\xE8s un certain nombre de tentatives d'acc\xE8s \xE9chou\xE9es"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "URL du webhook \xE0 appeler lorsque le secret est lu, d\xE9truit ou \xE9choue \xE0 \xEAtre lu (configurable, si d\xE9fini)",
          read: "Lecture",
          burn: "Destruction",
          failureToReadPK: "\xC9chec (mot de passe ou cl\xE9)",
          failureToReadIP: "\xC9chec (IP ou CIDR)",
          nameLabel: "Nom",
          namePlaceholder: "Entrez un nom pour ce webhook",
          nameDescription: "Un nom pour identifier ce webhook dans les notifications"
        }
      }
    },
    success: {
      title: "Secret Cr\xE9\xE9 !",
      description: {
        main: "Votre secret a \xE9t\xE9 cr\xE9\xE9 et l'URL a \xE9t\xE9 copi\xE9e dans le presse-papiers",
        password: "Partagez l'URL et le mot de passe avec le destinataire souhait\xE9"
      },
      urlCopied: "URL copi\xE9e dans le presse-papiers",
      qrCode: {
        title: "Code QR de l'URL du Secret",
        description: "T\xE9l\xE9chargez et partagez le code QR de l'URL du secret"
      },
      createAnother: "Cr\xE9er un Autre",
      deleteSecret: "Supprimer le Secret",
      info: {
        expires: "Expire dans: {{time}}",
        burn: "Le secret sera supprim\xE9 apr\xE8s sa lecture",
        passwordProtected: "Prot\xE9g\xE9 par mot de passe",
        ipRestrictions: "Restriction(s) IP: {{ips}}",
        readCount: "Nombre de lectures: {{count}}",
        webhook: "Webhook configur\xE9 pour: {{events}} ({{url}})",
        failureCount: "Le secret sera supprim\xE9 apr\xE8s {{count}} tentatives \xE9chou\xE9es"
      }
    },
    errors: {
      contentRequired: "Le contenu est requis",
      readCountWithBurn: "Le nombre de lectures ne peut pas \xEAtre utilis\xE9 avec la destruction apr\xE8s lecture",
      tooManyIps: "Trop de restrictions IP (maximum {{max}})",
      invalidIp: "Adresse IP ou bloc CIDR invalide : {{ip}}",
      uploadFailed: "Erreur lors du traitement du contenu t\xE9l\xE9charg\xE9 : {{error}}",
      deleteFailed: "Erreur lors de la suppression du secret : {{error}}",
      secretNotFound: "secret non trouv\xE9",
      unexpectedStatus: "code de statut inattendu {{code}}",
      webhookConfigInvalid: "La configuration du webhook est invalide - au moins un type d'\xE9v\xE9nement webhook est requis",
      fileSizeExceeded: "La taille du fichier d\xE9passe la taille maximale autoris\xE9e",
      fileReadError: "\xC9chec de la lecture du fichier",
      fileReadAborted: "La lecture du fichier a \xE9t\xE9 interrompue"
    }
  },
  view: {
    notFound: {
      title: "Secret Non Trouv\xE9",
      description: "Ce secret a peut-\xEAtre expir\xE9 ou a \xE9t\xE9 supprim\xE9.",
      createNew: "Cr\xE9er un Nouveau Secret"
    },
    password: {
      title: "Saisir le Mot de Passe",
      placeholder: "Saisissez le mot de passe",
      description: "Ce secret est prot\xE9g\xE9 par un mot de passe - demandez-le \xE0 l'exp\xE9diteur",
      error: "Mot de passe incorrect"
    },
    content: {
      fileShared: "Un fichier a \xE9t\xE9 partag\xE9 avec vous",
      downloadFile: "T\xE9l\xE9charger le Fichier",
      hideContent: "Masquer le contenu",
      showContent: "Afficher le contenu",
      copyToClipboard: "Copier dans le presse-papiers",
      copiedToClipboard: "Secret copi\xE9 dans le presse-papiers",
      clickToReveal: "Cliquez sur l'ic\xF4ne \u0153il ci-dessus pour r\xE9v\xE9ler le secret",
      passwordProtected: "Ce secret est prot\xE9g\xE9 par un mot de passe. Cliquez pour saisir le mot de passe.",
      clickToEnterPassword: "Cliquez pour saisir le mot de passe"
    },
    info: {
      burnedAfterReading: "Ce secret a \xE9t\xE9 supprim\xE9 apr\xE8s votre consultation et ne sera plus disponible apr\xE8s avoir quitt\xE9 la page.",
      expiresIn: "Expire {{time}}"
    },
    errors: {
      notFound: "non trouv\xE9",
      unexpectedStatus: "code de statut inattendu {{code}}"
    },
    actions: {
      viewSecret: "Voir le Secret"
    }
  },
  about: {
    title: "\xC0 Propos",
    intro: "crypt.fyi est une plateforme s\xE9curis\xE9e, open source et \xE9ph\xE9m\xE8re de partage de donn\xE9es qui vous permet de partager des informations sensibles en toute s\xE9curit\xE9. Qu'il s'agisse de mots de passe, de cl\xE9s API ou de messages confidentiels, crypt.fyi garantit que vos donn\xE9es restent priv\xE9es et disparaissent automatiquement apr\xE8s avoir \xE9t\xE9 consult\xE9es.",
    whyCryptFyi: {
      title: "Pourquoi crypt.fyi ?",
      commonPractices: {
        title: "Le Probl\xE8me avec les Pratiques Courantes",
        description: "Chaque jour, des informations sensibles comme des mots de passe, des cl\xE9s API et des donn\xE9es priv\xE9es sont partag\xE9es via des canaux non s\xE9curis\xE9s en texte clair :",
        problems: {
          email: "Email - peut \xEAtre intercept\xE9, stock\xE9 ind\xE9finiment et transf\xE9r\xE9 sans contr\xF4le",
          slack: "Messages Slack/Teams - restent dans l'historique des discussions et les journaux d'entreprise",
          sms: "SMS/Messages texte - stock\xE9s sur plusieurs appareils et serveurs d'op\xE9rateurs",
          messaging: "Messagerie instantan\xE9e - manque souvent de chiffrement ad\xE9quat et de suppression des donn\xE9es"
        }
      },
      existingSolutions: {
        title: "Solutions Existantes et Leurs Limites",
        description: "Bien qu'il existe d'autres outils dans ce domaine, chacun a ses limites :",
        limitations: {
          onePassword: "1Password - excellent pour la gestion des mots de passe en \xE9quipe, mais",
          onePasswordLink: "ne prend pas en charge le partage externe avec des utilisateurs non enregistr\xE9s",
          otherTools: "PrivateBin/PwPush/OneTimeSecret - fonctionnalit\xE9 de base similaire, mais interfaces utilisateur d\xE9pass\xE9es et piles technologiques anciennes, plus certaines",
          otherToolsConfigLink: "configurations",
          otherToolsSecurityLink: "fonctionnalit\xE9s de s\xE9curit\xE9"
        }
      },
      approach: {
        title: "L'Approche de crypt.fyi",
        description: "crypt.fyi a \xE9t\xE9 construit pour r\xE9pondre \xE0 ces d\xE9fis tout en adoptant les technologies web modernes. Il combine les principes de s\xE9curit\xE9 des solutions existantes avec une interface propre et intuitive et une pile technologique moderne. Le r\xE9sultat est un outil \xE0 la fois hautement s\xE9curis\xE9 et agr\xE9able \xE0 utiliser."
      }
    },
    howItWorks: {
      title: "Comment \xC7a Marche",
      steps: {
        encrypt: {
          title: "1. Chiffrer",
          description: "Votre secret est chiffr\xE9 directement dans votre navigateur avant de quitter votre appareil. Seules les personnes ayant le lien sp\xE9cial, que vous avez explicitement partag\xE9, peuvent le d\xE9chiffrer."
        },
        share: {
          title: "2. Partager",
          description: "Partagez le lien s\xE9curis\xE9 avec votre destinataire. Le lien contient tout ce qui est n\xE9cessaire pour d\xE9chiffrer le message, sauf si un mot de passe est sp\xE9cifi\xE9."
        },
        burn: {
          title: "3. D\xE9truire apr\xE8s lecture",
          description: 'Une fois consult\xE9, si "d\xE9truire apr\xE8s lecture" est coch\xE9, le secret est d\xE9finitivement supprim\xE9 de nos serveurs. Sans laisser de traces.'
        }
      }
    },
    security: {
      title: "Impl\xE9mentation de la S\xE9curit\xE9",
      encryption: {
        title: "Chiffrement de Bout en Bout",
        description: "Tous les secrets sont chiffr\xE9s en utilisant le chiffrement AES-256-GCM dans votre navigateur avant la transmission. La cl\xE9 de chiffrement ne quitte jamais votre appareil, assurant un v\xE9ritable chiffrement de bout en bout.",
        features: {
          key: "La cl\xE9 de chiffrement est d\xE9riv\xE9e d'une g\xE9n\xE9ration al\xE9atoire cryptographiquement s\xFBre",
          derivation: "La d\xE9rivation des cl\xE9s utilise PBKDF2 avec SHA-256",
          vector: "Chaque secret a un vecteur d'initialisation (IV) unique"
        }
      },
      zeroKnowledge: {
        title: "Architecture \xE0 Connaissance Nulle",
        description: "Nos serveurs ne voient jamais vos donn\xE9es non chiffr\xE9es. Nous employons une architecture \xE0 connaissance nulle o\xF9 :",
        features: {
          clientSide: "Tout le chiffrement/d\xE9chiffrement se fait c\xF4t\xE9 client",
          storage: "Les serveurs ne stockent que des donn\xE9es chiffr\xE9es",
          keys: "Les cl\xE9s de chiffrement sont transmises via des fragments d'URL, qui n'atteignent jamais le serveur API backend"
        }
      },
      protection: {
        title: "Protection des Donn\xE9es",
        description: "Plusieurs couches de s\xE9curit\xE9 assurent que vos donn\xE9es restent prot\xE9g\xE9es :",
        features: {
          encryption: "Chiffrement/d\xE9chiffrement c\xF4t\xE9 client",
          tls: "Chiffrement TLS pour toutes les communications API",
          destruction: "Destruction automatique du secret apr\xE8s consultation",
          logging: "Pas de journalisation des donn\xE9es sensibles sur le serveur",
          password: "Protection par mot de passe optionnelle pour une s\xE9curit\xE9 suppl\xE9mentaire"
        }
      }
    },
    openSource: {
      title: "Open Source",
      description: "crypt.fyi est open source et auditable. Vous pouvez examiner notre code, signaler des probl\xE8mes et contribuer sur"
    }
  },
  privacy: {
    title: "Politique de Confidentialit\xE9",
    intro: "Chez crypt.fyi, nous prenons votre confidentialit\xE9 au s\xE9rieux. Cette Politique de Confidentialit\xE9 explique comment nous g\xE9rons vos informations lorsque vous utilisez notre plateforme de partage de secrets \xE0 connaissance nulle et chiffrement de bout en bout.",
    doNotCollect: {
      title: "Informations que Nous ne Collectons pas",
      description: "En raison de notre architecture \xE0 connaissance nulle, nous sommes techniquement incapables d'acc\xE9der \xE0 :",
      items: {
        secrets: "Vos secrets ou fichiers non chiffr\xE9s",
        keys: "Cl\xE9s de chiffrement ou mots de passe",
        urls: "Fragments d'URL contenant des informations de d\xE9chiffrement",
        content: "Le contenu de vos donn\xE9es chiffr\xE9es",
        recipients: "Informations sur les destinataires de vos secrets"
      }
    },
    collect: {
      title: "Informations que Nous Collectons",
      description: "Nous collectons et stockons uniquement les informations minimales requises :",
      items: {
        encrypted: "Donn\xE9es chiffr\xE9es (que nous ne pouvons pas d\xE9chiffrer)",
        hashes: "Hachages de v\xE9rification des cl\xE9s (utilis\xE9s pour v\xE9rifier les droits d'acc\xE8s sans conna\xEEtre les cl\xE9s r\xE9elles)",
        metadata: "M\xE9tadonn\xE9es de base des requ\xEAtes (adresses IP, horodatages) pour la limitation des taux et la pr\xE9vention des abus",
        webhooks: "URLs de webhook si fournies (pour les notifications d'acc\xE8s et de suppression des secrets)"
      },
      note: "Toutes les donn\xE9es stock\xE9es sont automatiquement supprim\xE9es apr\xE8s expiration ou lors de l'acc\xE8s (si la destruction apr\xE8s lecture est activ\xE9e)."
    },
    usage: {
      title: "Comment Nous Utilisons les Informations",
      description: "Nous utilisons les informations collect\xE9es uniquement pour :",
      items: {
        transmission: "Faciliter la transmission s\xE9curis\xE9e de vos secrets chiffr\xE9s",
        rateLimits: "Appliquer des limites de taux pour pr\xE9venir les abus",
        ipControl: "Mettre en \u0153uvre la liste blanche IP/CIDR lorsque configur\xE9e",
        notifications: "Envoyer des notifications webhook lorsqu'activ\xE9es",
        security: "Maintenir la s\xE9curit\xE9 du syst\xE8me et pr\xE9venir les acc\xE8s non autoris\xE9s"
      }
    },
    security: {
      title: "Stockage et S\xE9curit\xE9 des Donn\xE9es",
      description: "Nos mesures de s\xE9curit\xE9 incluent :",
      items: {
        encryption: "Tout le chiffrement/d\xE9chiffrement se fait dans votre navigateur en utilisant AES-256-GCM",
        csp: "Politique de s\xE9curit\xE9 du contenu (CSP) stricte pour pr\xE9venir les attaques XSS",
        tls: "Chiffrement TLS pour toutes les communications API",
        expiration: "Expiration automatique des donn\xE9es avec dur\xE9e de vie configurable (TTL)",
        deletion: "Suppression s\xE9curis\xE9e des donn\xE9es apr\xE8s acc\xE8s ou expiration",
        storage: "Pas de stockage persistant d'informations sensibles"
      }
    },
    thirdParty: {
      title: "Services Tiers",
      description: "Nous ne partageons aucune information avec des tiers sauf dans les cas suivants :",
      items: {
        webhooks: "Lorsque vous activez les webhooks, nous envoyons des notifications aux URLs que vous fournissez concernant l'acc\xE8s et la suppression des secrets",
        infrastructure: "Fournisseurs d'infrastructure qui h\xE9bergent nos services (qui ne voient que des donn\xE9es chiffr\xE9es qu'ils ne peuvent pas d\xE9chiffrer)"
      }
    },
    rights: {
      title: "Vos Droits et Choix",
      description: "Vous avez le contr\xF4le sur vos donn\xE9es :",
      items: {
        expiration: "Choisir des d\xE9lais d'expiration personnalis\xE9s pour vos secrets",
        burn: "Activer la destruction apr\xE8s lecture pour une suppression imm\xE9diate apr\xE8s l'acc\xE8s",
        password: "Ajouter une protection par mot de passe pour une s\xE9curit\xE9 suppl\xE9mentaire",
        ip: "Configurer des restrictions IP pour contr\xF4ler l'acc\xE8s",
        readLimits: "D\xE9finir des limites de lecture"
      }
    },
    changes: {
      title: "Modifications de cette Politique",
      description: "Nous pouvons mettre \xE0 jour cette Politique de Confidentialit\xE9 de temps en temps. Nous informerons les utilisateurs de tout changement important en publiant la nouvelle Politique de Confidentialit\xE9 sur cette page. Nous vous conseillons de consulter r\xE9guli\xE8rement cette Politique de Confidentialit\xE9 pour tout changement."
    },
    contact: {
      title: "Nous Contacter",
      description: "Si vous avez des questions concernant cette Politique de Confidentialit\xE9, vous pouvez nous contacter via notre"
    }
  }
};

// src/i18n/locales/zh.ts
var zh = {
  common: {
    createSecret: "\u521B\u5EFA\u5BC6\u6587",
    starOnGithub: "\u5728 GitHub \u4E0A\u6807\u661F",
    features: "\u529F\u80FD\u7279\u70B9",
    learnMore: "\u4E86\u89E3\u66F4\u591A",
    loading: "\u52A0\u8F7D\u4E2D...",
    create: "\u521B\u5EFA",
    delete: "\u5220\u9664",
    copy: "\u590D\u5236",
    share: "\u5206\u4EAB",
    download: "\u4E0B\u8F7D",
    success: "\u6210\u529F\uFF01",
    error: "\u9519\u8BEF",
    confirm: "\u786E\u8BA4",
    requestNewLanguage: "\u8BF7\u6C42\u65B0\u8BED\u8A00\u6216\u4FEE\u6B63\u7FFB\u8BD1",
    footer: {
      tagline: "\u4EE5\u5B89\u5168\u548C\u9690\u79C1\u4E3A\u8BBE\u8BA1\u7406\u5FF5 - \u56E0\u4E3A\u65E0\u77E5\u4E5F\u662F\u4E00\u79CD\u5E78\u798F"
    },
    header: {
      tagline: "\u4F7F\u7528\u96F6\u77E5\u8BC6<aesLink>AES-256</aesLink><e2eLink>\u7AEF\u5230\u7AEF\u52A0\u5BC6</e2eLink>\u7684\u4E34\u65F6\u6570\u636E\u5206\u4EAB"
    },
    time: {
      minute_one: "{{count}} \u5206\u949F",
      minute_other: "{{count}} \u5206\u949F",
      hour_one: "{{count}} \u5C0F\u65F6",
      hour_other: "{{count}} \u5C0F\u65F6",
      day_one: "{{count}} \u5929",
      day_other: "{{count}} \u5929"
    }
  },
  landing: {
    title: "\u5B89\u5168\u5171\u4EAB\u654F\u611F\u6570\u636E",
    subtitle: "crypt.fyi \u652F\u6301\u96F6\u77E5\u8BC6\u7AEF\u5230\u7AEF\u52A0\u5BC6\u7684\u654F\u611F\u6570\u636E\u5171\u4EAB\uFF0C\u5E76\u63D0\u4F9B\u81EA\u52A8\u5220\u9664\u529F\u80FD",
    features: {
      encryption: {
        title: "\u7AEF\u5230\u7AEF\u52A0\u5BC6",
        description: "\u5728\u6570\u636E\u79BB\u5F00\u6D4F\u89C8\u5668\u4E4B\u524D\u4F7F\u7528 AES-256-GCM \u52A0\u5BC6\u786E\u4FDD\u5B89\u5168"
      },
      security: {
        title: "\u589E\u5F3A\u5B89\u5168\u6027",
        description: "\u4E25\u683C\u7684\u5185\u5BB9\u5B89\u5168\u7B56\u7565\uFF08CSP\uFF09\u548C\u901F\u7387\u9650\u5236\uFF0C\u4EE5\u51CF\u8F7B XSS \u548C\u66B4\u529B\u653B\u51FB"
      },
      zeroKnowledge: {
        title: "\u96F6\u77E5\u8BC6\u67B6\u6784",
        description: "\u6211\u4EEC\u7684\u670D\u52A1\u5668\u6C38\u8FDC\u4E0D\u4F1A\u770B\u5230\u60A8\u7684\u672A\u52A0\u5BC6\u6570\u636E - \u53EA\u6709\u60A8\u548C\u63A5\u6536\u8005\u53EF\u4EE5\u8BBF\u95EE"
      },
      burn: {
        title: "\u9605\u540E\u5373\u711A",
        description: "\u8BBF\u95EE\u540E\u81EA\u52A8\u9500\u6BC1\u5BC6\u6587\u4EE5\u786E\u4FDD\u6700\u5927\u5B89\u5168\u6027"
      },
      failedAttempts: {
        title: "\u5931\u8D25\u5C1D\u8BD5\u540E\u9500\u6BC1",
        description: "\u5728\u591A\u6B21\u8BBF\u95EE\u5931\u8D25\u5C1D\u8BD5\u540E\u81EA\u52A8\u9500\u6BC1\u5BC6\u6587\u4EE5\u589E\u5F3A\u5B89\u5168\u6027"
      },
      expiration: {
        title: "\u81EA\u52A8\u8FC7\u671F",
        description: "\u8BBE\u7F6E\u81EA\u5B9A\u4E49\u8FC7\u671F\u65F6\u95F4\uFF0C\u786E\u4FDD\u5BC6\u6587\u4E0D\u4F1A\u6301\u7EED\u8D85\u8FC7\u6240\u9700\u65F6\u95F4"
      },
      password: {
        title: "\u5BC6\u7801\u4FDD\u62A4",
        description: "\u901A\u8FC7\u53EF\u9009\u7684\u5BC6\u7801\u4FDD\u62A4\u6DFB\u52A0\u989D\u5916\u7684\u5B89\u5168\u5C42"
      },
      files: {
        title: "\u6587\u4EF6\u5171\u4EAB",
        description: "\u4F7F\u7528\u7B80\u5355\u7684\u62D6\u653E\u529F\u80FD\u5B89\u5168\u5730\u5171\u4EAB\u6587\u4EF6"
      },
      webhooks: {
        title: "Webhook \u901A\u77E5",
        description: "\u5F53\u60A8\u7684\u5BC6\u6587\u88AB\u8BBF\u95EE\u3001\u9500\u6BC1\u6216\u65E0\u6CD5\u8BFB\u53D6\u65F6\u83B7\u5F97\u901A\u77E5"
      },
      ipControl: {
        title: "IP \u9650\u5236",
        description: "\u901A\u8FC7\u9650\u5236\u53EF\u4EE5\u67E5\u770B\u5BC6\u6587\u7684 IP \u5730\u5740\u6216 CIDR \u8303\u56F4\u6765\u63A7\u5236\u8BBF\u95EE"
      },
      readLimits: {
        title: "\u9605\u8BFB\u9650\u5236",
        description: "\u8BBE\u7F6E\u6700\u5927\u67E5\u770B\u6B21\u6570\u4EE5\u9650\u5236\u5BC6\u6587\u53EF\u88AB\u8BBF\u95EE\u7684\u6B21\u6570"
      },
      qrCode: {
        title: "\u4E8C\u7EF4\u7801",
        description: "\u751F\u6210\u4E8C\u7EF4\u7801\u4EE5\u4FBF\u5728\u79FB\u52A8\u8BBE\u5907\u4E0A\u8F7B\u677E\u5171\u4EAB\u60A8\u7684\u5BC6\u6587 URL"
      },
      cli: {
        title: "CLI \u5DE5\u5177",
        description: "\u4F7F\u7528\u6211\u4EEC\u7684\u547D\u4EE4\u884C\u754C\u9762\u81EA\u52A8\u5316\u5BC6\u6587\u5171\u4EAB"
      },
      chromeExtension: {
        title: "Chrome \u6269\u5C55",
        description: "\u4F7F\u7528\u6211\u4EEC\u7684 Chrome \u6269\u5C55\u76F4\u63A5\u4ECE\u6D4F\u89C8\u5668\u5171\u4EAB\u5BC6\u6587"
      },
      docker: {
        title: "Docker \u652F\u6301",
        description: "\u4F7F\u7528\u6211\u4EEC\u7684\u5B98\u65B9 Docker \u955C\u50CF\u90E8\u7F72\u60A8\u81EA\u5DF1\u7684\u5B9E\u4F8B"
      }
    },
    steps: {
      encrypt: {
        title: "1. \u52A0\u5BC6",
        description: "\u60A8\u7684\u5BC6\u6587\u5728\u5B58\u4E4B\u524D\u4F1A\u5728\u6D4F\u89C8\u5668\u4E2D\u8FDB\u884C\u52A0\u5BC6"
      },
      share: {
        title: "2. \u5206\u4EAB",
        description: "\u5C06\u5B89\u5168\u94FE\u63A5\u53D1\u9001\u7ED9\u9884\u671F\u63A5\u6536\u8005"
      },
      autoDelete: {
        title: "3. \u81EA\u52A8\u5220\u9664",
        description: "\u5BC6\u6587\u5728\u88AB\u67E5\u770B\u540E\u5C06\u6C38\u4E45\u5220\u9664"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "\u5BC6\u6587\u5185\u5BB9",
        placeholder: "\u5728\u6B64\u8F93\u5165\u60A8\u7684\u5BC6\u6587\u5185\u5BB9...",
        fileHint: "\u62D6\u653E\u6216\u70B9\u51FB\u6B64\u5904\u6DFB\u52A0\u6587\u4EF6",
        fileSelected: "\u5DF2\u9009\u62E9\u6587\u4EF6\uFF1A{{name}}\uFF08{{size}} KB\uFF09"
      },
      password: {
        label: "\u5BC6\u7801",
        placeholder: "\u53EF\u9009\uFF08\u4F46\u5EFA\u8BAE\u4F7F\u7528\uFF09"
      },
      ttl: {
        label: "\u6709\u6548\u671F",
        placeholder: "\u9009\u62E9\u8FC7\u671F\u65F6\u95F4"
      },
      burn: {
        label: "\u9605\u540E\u5373\u711A",
        description: "\u786E\u4FDD\u53EA\u6709\u4E00\u4E2A\u63A5\u6536\u8005\u53EF\u4EE5\u8BBF\u95EE\u5BC6\u6587"
      },
      advanced: {
        toggle: "\u9AD8\u7EA7\u914D\u7F6E",
        ip: {
          label: "IP/CIDR \u767D\u540D\u5355",
          placeholder: "192.168.1.1, 10.0.0.0/24, \u7B49",
          description: "\u9650\u5236\u7279\u5B9A IP \u5730\u5740\u6216 CIDR \u5757\u7684\u8BBF\u95EE\uFF08\u9017\u53F7\u5206\u9694\uFF09"
        },
        readCount: {
          label: "\u9605\u8BFB\u6B21\u6570",
          description: "\u5BC6\u6587\u53EF\u88AB\u9605\u8BFB\u7684\u6700\u5927\u6B21\u6570"
        },
        failedAttempts: {
          label: "\u5931\u8D25\u5C1D\u8BD5\u540E\u9500\u6BC1",
          description: "\u5728\u591A\u6B21\u8BBF\u95EE\u5931\u8D25\u5C1D\u8BD5\u540E\u81EA\u52A8\u9500\u6BC1\u5BC6\u6587"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "\u5F53\u5BC6\u6587\u88AB\u8BFB\u53D6\u3001\u9500\u6BC1\u6216\u8BFB\u53D6\u5931\u8D25\u65F6\u8C03\u7528\u7684 Webhook URL\uFF08\u5982\u679C\u8BBE\u7F6E\uFF0C\u53EF\u914D\u7F6E\uFF09",
          read: "\u8BFB\u53D6",
          burn: "\u9500\u6BC1",
          failureToReadPK: "\u5931\u8D25\uFF08\u5BC6\u7801\u6216\u5BC6\u94A5\uFF09",
          failureToReadIP: "\u5931\u8D25\uFF08IP \u6216 CIDR\uFF09",
          nameLabel: "\u540D\u79F0",
          namePlaceholder: "\u8F93\u5165\u6B64 webhook \u7684\u540D\u79F0",
          nameDescription: "\u7528\u4E8E\u5728\u901A\u77E5\u4E2D\u8BC6\u522B\u6B64 webhook \u7684\u540D\u79F0"
        }
      }
    },
    success: {
      title: "\u5BC6\u6587\u5DF2\u521B\u5EFA\uFF01",
      description: {
        main: "\u60A8\u7684\u5BC6\u6587\u5DF2\u521B\u5EFA\uFF0CURL \u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
        password: "\u4E0E\u9884\u671F\u63A5\u6536\u8005\u5206\u4EAB URL \u548C\u5BC6\u7801"
      },
      urlCopied: "URL \u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
      qrCode: {
        title: "\u5BC6\u6587 URL \u4E8C\u7EF4\u7801",
        description: "\u4E0B\u8F7D\u5E76\u5206\u4EAB\u5BC6\u6587 URL \u4E8C\u7EF4\u7801"
      },
      createAnother: "\u521B\u5EFA\u53E6\u4E00\u4E2A",
      deleteSecret: "\u5220\u9664\u5BC6\u6587",
      info: {
        expires: "\u8FC7\u671F\u65F6\u95F4\uFF1A{{time}}",
        burn: "\u5BC6\u6587\u5C06\u5728\u67E5\u770B\u540E\u5220\u9664",
        passwordProtected: "\u5BC6\u7801\u4FDD\u62A4",
        ipRestrictions: "IP\u9650\u5236\uFF1A{{ips}}",
        readCount: "\u9605\u8BFB\u6B21\u6570\uFF1A{{count}}",
        webhook: "Webhook\u5DF2\u914D\u7F6E\uFF1A{{events}} ({{url}})",
        failureCount: "\u5BC6\u6587\u5C06\u5728{{count}}\u6B21\u5931\u8D25\u5C1D\u8BD5\u540E\u5220\u9664"
      }
    },
    errors: {
      contentRequired: "\u5185\u5BB9\u4E3A\u5FC5\u586B\u9879",
      readCountWithBurn: "\u9605\u540E\u5373\u711A\u6A21\u5F0F\u4E0B\u4E0D\u80FD\u8BBE\u7F6E\u9605\u8BFB\u6B21\u6570",
      tooManyIps: "IP \u9650\u5236\u8FC7\u591A\uFF08\u6700\u5927 {{max}}\uFF09",
      invalidIp: "\u65E0\u6548\u7684 IP \u5730\u5740\u6216 CIDR \u5757\uFF1A{{ip}}",
      uploadFailed: "\u5904\u7406\u4E0A\u4F20\u5185\u5BB9\u5931\u8D25\uFF1A{{error}}",
      deleteFailed: "\u5220\u9664\u5BC6\u6587\u5931\u8D25\uFF1A{{error}}",
      secretNotFound: "\u672A\u627E\u5230\u5BC6\u6587",
      unexpectedStatus: "\u610F\u5916\u7684\u72B6\u6001\u7801 {{code}}",
      webhookConfigInvalid: "Webhook\u914D\u7F6E\u65E0\u6548 - \u81F3\u5C11\u9700\u8981\u4E00\u4E2AWebhook\u4E8B\u4EF6\u7C7B\u578B",
      fileSizeExceeded: "\u6587\u4EF6\u5927\u5C0F\u8D85\u8FC7\u6700\u5927\u5141\u8BB8\u5927\u5C0F",
      fileReadError: "\u8BFB\u53D6\u6587\u4EF6\u5931\u8D25",
      fileReadAborted: "\u6587\u4EF6\u8BFB\u53D6\u5DF2\u4E2D\u6B62"
    }
  },
  view: {
    notFound: {
      title: "\u672A\u627E\u5230\u5BC6\u6587",
      description: "\u6B64\u5BC6\u6587\u53EF\u80FD\u5DF2\u8FC7\u671F\u6216\u88AB\u5220\u9664\u3002",
      createNew: "\u521B\u5EFA\u65B0\u5BC6\u6587"
    },
    password: {
      title: "\u8F93\u5165\u5BC6\u7801",
      placeholder: "\u8F93\u5165\u5BC6\u7801",
      description: "\u6B64\u5BC6\u6587\u53D7\u5BC6\u7801\u4FDD\u62A4 - \u8BF7\u5411\u53D1\u9001\u8005\u7D22\u8981\u5BC6\u7801",
      error: "\u5BC6\u7801\u9519\u8BEF"
    },
    content: {
      fileShared: "\u6709\u4EBA\u4E0E\u60A8\u5206\u4EAB\u4E86\u4E00\u4E2A\u6587\u4EF6",
      downloadFile: "\u4E0B\u8F7D\u6587\u4EF6",
      hideContent: "\u9690\u85CF\u5185\u5BB9",
      showContent: "\u663E\u793A\u5185\u5BB9",
      copyToClipboard: "\u590D\u5236\u5230\u526A\u8D34\u677F",
      copiedToClipboard: "\u5BC6\u6587\u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
      clickToReveal: "\u70B9\u51FB\u4E0A\u65B9\u7684\u773C\u775B\u56FE\u6807\u663E\u793A\u5BC6\u6587",
      passwordProtected: "\u6B64\u5BC6\u6587\u53D7\u5BC6\u7801\u4FDD\u62A4\u3002\u70B9\u51FB\u8F93\u5165\u5BC6\u7801\u3002",
      clickToEnterPassword: "\u70B9\u51FB\u8F93\u5165\u5BC6\u7801"
    },
    info: {
      burnedAfterReading: "\u6B64\u5BC6\u6587\u5728\u60A8\u67E5\u770B\u540E\u5DF2\u88AB\u5220\u9664\uFF0C\u79BB\u5F00\u9875\u9762\u540E\u5C06\u65E0\u6CD5\u518D\u6B21\u8BBF\u95EE\u3002",
      expiresIn: "{{time}}\u540E\u8FC7\u671F"
    },
    errors: {
      notFound: "\u672A\u627E\u5230",
      unexpectedStatus: "\u610F\u5916\u7684\u72B6\u6001\u7801 {{code}}"
    },
    actions: {
      viewSecret: "\u67E5\u770B\u5BC6\u6587"
    }
  },
  about: {
    title: "\u5173\u4E8E",
    intro: "crypt.fyi \u662F\u4E00\u4E2A\u5B89\u5168\u3001\u5F00\u6E90\u3001\u4E34\u65F6\u7684\u6570\u636E\u5206\u4EAB\u5E73\u53F0\uFF0C\u4F7F\u60A8\u80FD\u591F\u5B89\u5168\u5730\u5206\u4EAB\u654F\u611F\u4FE1\u606F\u3002\u65E0\u8BBA\u662F\u5BC6\u7801\u3001API \u5BC6\u94A5\u8FD8\u662F\u673A\u5BC6\u6D88\u606F\uFF0Ccrypt.fyi \u90FD\u80FD\u786E\u4FDD\u60A8\u7684\u6570\u636E\u4FDD\u6301\u79C1\u5BC6\uFF0C\u5E76\u5728\u88AB\u8BBF\u95EE\u540E\u81EA\u52A8\u6D88\u5931\u3002",
    whyCryptFyi: {
      title: "\u4E3A\u4EC0\u4E48\u9009\u62E9 crypt.fyi\uFF1F",
      commonPractices: {
        title: "\u5E38\u89C1\u505A\u6CD5\u7684\u95EE\u9898",
        description: "\u6BCF\u5929\uFF0C\u654F\u611F\u4FE1\u606F\u5982\u5BC6\u7801\u3001API \u5BC6\u94A5\u548C\u79C1\u5BC6\u6570\u636E\u90FD\u4EE5\u660E\u6587\u5F62\u5F0F\u901A\u8FC7\u4E0D\u5B89\u5168\u7684\u6E20\u9053\u5171\u4EAB\uFF1A",
        problems: {
          email: "\u7535\u5B50\u90AE\u4EF6 - \u53EF\u80FD\u88AB\u62E6\u622A\u3001\u65E0\u9650\u671F\u5B58\u50A8\u548C\u8F6C\u53D1",
          slack: "Slack/Teams \u6D88\u606F - \u4FDD\u7559\u5728\u804A\u5929\u5386\u53F2\u548C\u516C\u53F8\u65E5\u5FD7\u4E2D",
          sms: "\u77ED\u4FE1 - \u5B58\u50A8\u5728\u591A\u4E2A\u8BBE\u5907\u548C\u8FD0\u8425\u5546\u670D\u52A1\u5668\u4E0A",
          messaging: "\u5373\u65F6\u901A\u8BAF - \u901A\u5E38\u7F3A\u4E4F\u9002\u5F53\u7684\u52A0\u5BC6\u548C\u6570\u636E\u5220\u9664"
        }
      },
      existingSolutions: {
        title: "\u73B0\u6709\u89E3\u51B3\u65B9\u6848\u53CA\u5176\u5C40\u9650\u6027",
        description: "\u867D\u7136\u8FD9\u4E2A\u9886\u57DF\u6709\u5176\u4ED6\u5DE5\u5177\uFF0C\u4F46\u6BCF\u4E2A\u90FD\u6709\u5176\u5C40\u9650\u6027\uFF1A",
        limitations: {
          onePassword: "1Password - \u51FA\u8272\u7684\u56E2\u961F\u5BC6\u7801\u7BA1\u7406\u5DE5\u5177\uFF0C\u4F46",
          onePasswordLink: "\u4E0D\u652F\u6301\u4E0E\u5916\u90E8\u975E\u7528\u6237\u5171\u4EAB",
          otherTools: "PrivateBin/PwPush/OneTimeSecret - \u6838\u5FC3\u529F\u80FD\u76F8\u4F3C\uFF0C\u4F46\u7528\u6237\u754C\u9762\u548C\u6280\u672F\u6808\u8F83\u65E7\uFF0C\u4E14\u7F3A\u5C11\u4E00\u4E9B",
          otherToolsConfigLink: "\u53EF\u914D\u7F6E\u6027",
          otherToolsSecurityLink: "\u5B89\u5168\u529F\u80FD"
        }
      },
      approach: {
        title: "crypt.fyi \u7684\u65B9\u6CD5",
        description: "crypt.fyi \u5728\u62E5\u62B1\u73B0\u4EE3\u7F51\u7EDC\u6280\u672F\u7684\u540C\u65F6\u89E3\u51B3\u8FD9\u4E9B\u6311\u6218\u3002\u5B83\u5C06\u73B0\u6709\u89E3\u51B3\u65B9\u6848\u7684\u5B89\u5168\u539F\u5219\u4E0E\u6E05\u6670\u3001\u76F4\u89C2\u7684\u754C\u9762\u548C\u73B0\u4EE3\u6280\u672F\u6808\u76F8\u7ED3\u5408\u3002\u7ED3\u679C\u662F\u4E00\u4E2A\u65E2\u9AD8\u5EA6\u5B89\u5168\u53C8\u6613\u4E8E\u4F7F\u7528\u7684\u5DE5\u5177\u3002"
      }
    },
    howItWorks: {
      title: "\u5DE5\u4F5C\u539F\u7406",
      steps: {
        encrypt: {
          title: "1. \u52A0\u5BC6",
          description: "\u60A8\u7684\u5BC6\u6587\u5728\u79BB\u5F00\u8BBE\u5907\u4E4B\u524D\u5C31\u5728\u6D4F\u89C8\u5668\u4E2D\u52A0\u5BC6\u3002\u53EA\u6709\u62E5\u6709\u60A8\u660E\u786E\u5171\u4EAB\u7684\u7279\u6B8A\u94FE\u63A5\u7684\u4EBA\u624D\u80FD\u89E3\u5BC6\u3002"
        },
        share: {
          title: "2. \u5206\u4EAB",
          description: "\u4E0E\u9884\u671F\u63A5\u6536\u8005\u5206\u4EAB\u5B89\u5168\u94FE\u63A5\u3002\u9664\u975E\u6307\u5B9A\u5BC6\u7801\uFF0C\u5426\u5219\u94FE\u63A5\u5305\u542B\u89E3\u5BC6\u6D88\u606F\u6240\u9700\u7684\u4E00\u5207\u3002"
        },
        burn: {
          title: "3. \u9605\u540E\u5373\u711A",
          description: '\u4E00\u65E6\u88AB\u8BBF\u95EE\uFF0C\u5982\u679C\u9009\u4E2D\u4E86"\u9605\u540E\u5373\u711A"\uFF0C\u5BC6\u6587\u5C06\u4ECE\u6211\u4EEC\u7684\u670D\u52A1\u5668\u4E0A\u6C38\u4E45\u5220\u9664\u3002\u4E0D\u7559\u4EFB\u4F55\u75D5\u8FF9\u3002'
        }
      }
    },
    security: {
      title: "\u5B89\u5168\u5B9E\u73B0",
      encryption: {
        title: "\u7AEF\u5230\u7AEF\u52A0\u5BC6",
        description: "\u6240\u6709\u5BC6\u6587\u5728\u4F20\u8F93\u524D\u90FD\u4F7F\u7528 AES-256-GCM \u52A0\u5BC6\u5728\u60A8\u7684\u6D4F\u89C8\u5668\u4E2D\u8FDB\u884C\u52A0\u5BC6\u3002\u52A0\u5BC6\u5BC6\u94A5\u6C38\u8FDC\u4E0D\u4F1A\u79BB\u5F00\u60A8\u7684\u8BBE\u5907\uFF0C\u786E\u4FDD\u771F\u6B63\u7684\u7AEF\u5230\u7AEF\u52A0\u5BC6\u3002",
        features: {
          key: "\u52A0\u5BC6\u5BC6\u94A5\u6765\u81EA\u5BC6\u7801\u5B66\u5B89\u5168\u7684\u968F\u673A\u751F\u6210",
          derivation: "\u5BC6\u94A5\u6D3E\u751F\u4F7F\u7528 PBKDF2 \u548C SHA-256",
          vector: "\u6BCF\u4E2A\u5BC6\u6587\u90FD\u6709\u552F\u4E00\u7684\u521D\u59CB\u5316\u5411\u91CF\uFF08IV\uFF09"
        }
      },
      zeroKnowledge: {
        title: "\u96F6\u77E5\u8BC6\u67B6\u6784",
        description: "\u6211\u4EEC\u7684\u670D\u52A1\u5668\u6C38\u8FDC\u4E0D\u4F1A\u770B\u5230\u60A8\u7684\u672A\u52A0\u5BC6\u6570\u636E\u3002\u6211\u4EEC\u91C7\u7528\u96F6\u77E5\u8BC6\u67B6\u6784\uFF1A",
        features: {
          clientSide: "\u6240\u6709\u52A0\u5BC6/\u89E3\u5BC6\u90FD\u5728\u5BA2\u6237\u7AEF\u8FDB\u884C",
          storage: "\u670D\u52A1\u5668\u53EA\u5B58\u50A8\u52A0\u5BC6\u6570\u636E",
          keys: "\u52A0\u5BC6\u5BC6\u94A5\u901A\u8FC7 URL \u7247\u6BB5\u4F20\u8F93\uFF0C\u6C38\u8FDC\u4E0D\u4F1A\u5230\u8FBE\u540E\u7AEF API \u670D\u52A1\u5668"
        }
      },
      protection: {
        title: "\u6570\u636E\u4FDD\u62A4",
        description: "\u591A\u5C42\u5B89\u5168\u786E\u4FDD\u60A8\u7684\u6570\u636E\u53D7\u5230\u4FDD\u62A4\uFF1A",
        features: {
          encryption: "\u5BA2\u6237\u7AEF\u52A0\u5BC6/\u89E3\u5BC6",
          tls: "\u6240\u6709 API \u901A\u4FE1\u90FD\u4F7F\u7528 TLS \u52A0\u5BC6",
          destruction: "\u8BBF\u95EE\u540E\u81EA\u52A8\u9500\u6BC1\u5BC6\u6587",
          logging: "\u4E0D\u8BB0\u5F55\u654F\u611F\u6570\u636E\u7684\u670D\u52A1\u5668\u65E5\u5FD7",
          password: "\u53EF\u9009\u7684\u5BC6\u7801\u4FDD\u62A4\u4EE5\u63D0\u4F9B\u989D\u5916\u5B89\u5168\u6027"
        }
      }
    },
    openSource: {
      title: "\u5F00\u6E90",
      description: "crypt.fyi \u662F\u5F00\u6E90\u4E14\u53EF\u5BA1\u8BA1\u7684\u3002\u60A8\u53EF\u4EE5\u5728\u4EE5\u4E0B\u4F4D\u7F6E\u67E5\u770B\u6211\u4EEC\u7684\u4EE3\u7801\u3001\u63D0\u4EA4\u95EE\u9898\u548C\u8D21\u732E"
    }
  },
  privacy: {
    title: "\u9690\u79C1\u653F\u7B56",
    intro: "\u5728 crypt.fyi\uFF0C\u6211\u4EEC\u4E25\u8083\u5BF9\u5F85\u60A8\u7684\u9690\u79C1\u3002\u672C\u9690\u79C1\u653F\u7B56\u8BF4\u660E\u4E86\u5F53\u60A8\u4F7F\u7528\u6211\u4EEC\u7684\u96F6\u77E5\u8BC6\u3001\u7AEF\u5230\u7AEF\u52A0\u5BC6\u7684\u79D8\u5BC6\u5171\u4EAB\u5E73\u53F0\u65F6\uFF0C\u6211\u4EEC\u5982\u4F55\u5904\u7406\u60A8\u7684\u4FE1\u606F\u3002",
    doNotCollect: {
      title: "\u6211\u4EEC\u4E0D\u6536\u96C6\u7684\u4FE1\u606F",
      description: "\u7531\u4E8E\u6211\u4EEC\u7684\u96F6\u77E5\u8BC6\u67B6\u6784\uFF0C\u6211\u4EEC\u5728\u6280\u672F\u4E0A\u65E0\u6CD5\u8BBF\u95EE\uFF1A",
      items: {
        secrets: "\u60A8\u7684\u672A\u52A0\u5BC6\u79D8\u5BC6\u6216\u6587\u4EF6",
        keys: "\u52A0\u5BC6\u5BC6\u94A5\u6216\u5BC6\u7801",
        urls: "\u5305\u542B\u89E3\u5BC6\u4FE1\u606F\u7684 URL \u7247\u6BB5",
        content: "\u60A8\u7684\u52A0\u5BC6\u6570\u636E\u7684\u5185\u5BB9",
        recipients: "\u60A8\u7684\u79D8\u5BC6\u63A5\u6536\u8005\u7684\u4FE1\u606F"
      }
    },
    collect: {
      title: "\u6211\u4EEC\u6536\u96C6\u7684\u4FE1\u606F",
      description: "\u6211\u4EEC\u4EC5\u6536\u96C6\u548C\u5B58\u50A8\u6700\u5C11\u5FC5\u9700\u7684\u4FE1\u606F\uFF1A",
      items: {
        encrypted: "\u52A0\u5BC6\u6570\u636E\uFF08\u6211\u4EEC\u65E0\u6CD5\u89E3\u5BC6\uFF09",
        hashes: "\u5BC6\u94A5\u9A8C\u8BC1\u54C8\u5E0C\uFF08\u7528\u4E8E\u9A8C\u8BC1\u8BBF\u95EE\u6743\u9650\u800C\u65E0\u9700\u77E5\u9053\u5B9E\u9645\u5BC6\u94A5\uFF09",
        metadata: "\u57FA\u672C\u8BF7\u6C42\u5143\u6570\u636E\uFF08IP \u5730\u5740\u3001\u65F6\u95F4\u6233\uFF09\u7528\u4E8E\u901F\u7387\u9650\u5236\u548C\u6EE5\u7528\u9884\u9632",
        webhooks: "\u5982\u679C\u63D0\u4F9B\u4E86 webhook URL\uFF08\u7528\u4E8E\u79D8\u5BC6\u8BBF\u95EE\u548C\u5220\u9664\u901A\u77E5\uFF09"
      },
      note: "\u6240\u6709\u5B58\u50A8\u7684\u6570\u636E\u5728\u8FC7\u671F\u540E\u6216\u8BBF\u95EE\u65F6\u81EA\u52A8\u5220\u9664\uFF08\u5982\u679C\u542F\u7528\u4E86\u9605\u540E\u5373\u711A\uFF09\u3002"
    },
    usage: {
      title: "\u6211\u4EEC\u5982\u4F55\u4F7F\u7528\u4FE1\u606F",
      description: "\u6211\u4EEC\u4EC5\u5C06\u6536\u96C6\u7684\u4FE1\u606F\u7528\u4E8E\uFF1A",
      items: {
        transmission: "\u4FC3\u8FDB\u60A8\u7684\u52A0\u5BC6\u79D8\u5BC6\u7684\u5B89\u5168\u4F20\u8F93",
        rateLimits: "\u5B9E\u65BD\u901F\u7387\u9650\u5236\u4EE5\u9632\u6B62\u6EE5\u7528",
        ipControl: "\u5728\u914D\u7F6E\u65F6\u5B9E\u65BD IP/CIDR \u767D\u540D\u5355",
        notifications: "\u5728\u542F\u7528\u65F6\u53D1\u9001 webhook \u901A\u77E5",
        security: "\u7EF4\u62A4\u7CFB\u7EDF\u5B89\u5168\u5E76\u9632\u6B62\u672A\u6388\u6743\u8BBF\u95EE"
      }
    },
    security: {
      title: "\u6570\u636E\u5B58\u50A8\u548C\u5B89\u5168",
      description: "\u6211\u4EEC\u7684\u5B89\u5168\u63AA\u65BD\u5305\u62EC\uFF1A",
      items: {
        encryption: "\u6240\u6709\u52A0\u5BC6/\u89E3\u5BC6\u90FD\u5728\u60A8\u7684\u6D4F\u89C8\u5668\u4E2D\u4F7F\u7528 AES-256-GCM \u8FDB\u884C",
        csp: "\u4E25\u683C\u7684\u5185\u5BB9\u5B89\u5168\u7B56\u7565 (CSP) \u4EE5\u9632\u6B62 XSS \u653B\u51FB",
        tls: "\u6240\u6709 API \u901A\u4FE1\u90FD\u4F7F\u7528 TLS \u52A0\u5BC6",
        expiration: "\u5177\u6709\u53EF\u914D\u7F6E\u751F\u5B58\u65F6\u95F4 (TTL) \u7684\u81EA\u52A8\u6570\u636E\u8FC7\u671F",
        deletion: "\u8BBF\u95EE\u6216\u8FC7\u671F\u540E\u5B89\u5168\u5220\u9664\u6570\u636E",
        storage: "\u4E0D\u6301\u4E45\u5B58\u50A8\u654F\u611F\u4FE1\u606F"
      }
    },
    thirdParty: {
      title: "\u7B2C\u4E09\u65B9\u670D\u52A1",
      description: "\u9664\u4EE5\u4E0B\u60C5\u51B5\u5916\uFF0C\u6211\u4EEC\u4E0D\u4E0E\u7B2C\u4E09\u65B9\u5171\u4EAB\u4FE1\u606F\uFF1A",
      items: {
        webhooks: "\u5F53\u60A8\u542F\u7528 webhooks \u65F6\uFF0C\u6211\u4EEC\u5411\u60A8\u63D0\u4F9B\u7684 URL \u53D1\u9001\u6709\u5173\u79D8\u5BC6\u8BBF\u95EE\u548C\u5220\u9664\u7684\u901A\u77E5",
        infrastructure: "\u6258\u7BA1\u6211\u4EEC\u670D\u52A1\u7684\u57FA\u7840\u8BBE\u65BD\u63D0\u4F9B\u5546\uFF08\u4ED6\u4EEC\u53EA\u80FD\u770B\u5230\u65E0\u6CD5\u89E3\u5BC6\u7684\u52A0\u5BC6\u6570\u636E\uFF09"
      }
    },
    rights: {
      title: "\u60A8\u7684\u6743\u5229\u548C\u9009\u62E9",
      description: "\u60A8\u53EF\u4EE5\u63A7\u5236\u60A8\u7684\u6570\u636E\uFF1A",
      items: {
        expiration: "\u4E3A\u60A8\u7684\u79D8\u5BC6\u9009\u62E9\u81EA\u5B9A\u4E49\u8FC7\u671F\u65F6\u95F4",
        burn: "\u542F\u7528\u9605\u540E\u5373\u711A\u4EE5\u5728\u8BBF\u95EE\u540E\u7ACB\u5373\u5220\u9664",
        password: "\u6DFB\u52A0\u5BC6\u7801\u4FDD\u62A4\u4EE5\u589E\u52A0\u5B89\u5168\u6027",
        ip: "\u914D\u7F6E IP \u9650\u5236\u4EE5\u63A7\u5236\u8BBF\u95EE",
        readLimits: "\u8BBE\u7F6E\u9605\u8BFB\u9650\u5236"
      }
    },
    changes: {
      title: "\u672C\u653F\u7B56\u7684\u53D8\u66F4",
      description: "\u6211\u4EEC\u53EF\u80FD\u4F1A\u4E0D\u65F6\u66F4\u65B0\u672C\u9690\u79C1\u653F\u7B56\u3002\u6211\u4EEC\u4F1A\u901A\u8FC7\u5728\u6B64\u9875\u9762\u4E0A\u53D1\u5E03\u65B0\u7684\u9690\u79C1\u653F\u7B56\u6765\u901A\u77E5\u7528\u6237\u91CD\u5927\u53D8\u66F4\u3002\u6211\u4EEC\u5EFA\u8BAE\u60A8\u5B9A\u671F\u67E5\u770B\u672C\u9690\u79C1\u653F\u7B56\u4EE5\u4E86\u89E3\u4EFB\u4F55\u53D8\u66F4\u3002"
    },
    contact: {
      title: "\u8054\u7CFB\u6211\u4EEC",
      description: "\u5982\u679C\u60A8\u5BF9\u672C\u9690\u79C1\u653F\u7B56\u6709\u4EFB\u4F55\u95EE\u9898\uFF0C\u60A8\u53EF\u4EE5\u901A\u8FC7\u6211\u4EEC\u7684"
    }
  }
};

// src/i18n/index.ts
var resources = {
  en: {
    translation: en
  },
  es: {
    translation: es
  },
  de: {
    translation: de
  },
  fr: {
    translation: fr
  },
  zh: {
    translation: zh
  }
};
var supportedLanguages = {
  en: "English",
  es: "Espa\xF1ol",
  de: "Deutsch",
  fr: "Fran\xE7ais",
  zh: "\u4E2D\u6587"
};
var supportedLanguagesOptions = Object.entries(supportedLanguages).map(([key, value]) => ({
  value: key,
  label: value
}));

// src/client.ts
import { inflate, deflate } from "pako";

// src/buffer.ts
import { Buffer as Buffer2 } from "buffer";

// src/random.ts
import { randomBytes } from "@noble/hashes/utils";
async function generateRandomBytes(length) {
  return randomBytes(length);
}
async function generateRandomHexString(length) {
  const bytes = await generateRandomBytes(length);
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
var URL_SAFE_DICTIONARY = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_~`;
async function generateRandomString(length, dictionary = URL_SAFE_DICTIONARY) {
  const dictionaryLength = dictionary.length;
  const maxValid = Math.floor(256 / dictionaryLength) * dictionaryLength - 1;
  let result = "";
  while (result.length < length) {
    const bytes = await generateRandomBytes(length - result.length);
    for (const byte of bytes) {
      if (byte > maxValid) continue;
      result += dictionary.charAt(byte % dictionaryLength);
      if (result.length === length) break;
    }
  }
  return result;
}

// src/encryption/gcm.ts
var gcm_exports = {};
__export(gcm_exports, {
  decrypt: () => decrypt,
  encrypt: () => encrypt
});
import { gcm } from "@noble/ciphers/aes";
import { pbkdf2Async } from "@noble/hashes/pbkdf2";
import { sha256 as nobleSha256 } from "@noble/hashes/sha256";
import { randomBytes as randomBytes2 } from "@noble/hashes/utils";
import { concatBytes, utf8ToBytes } from "@noble/hashes/utils";

// src/encryption/encryption.ts
var DecryptError = class extends Error {
  constructor(error) {
    super("decrypt error");
    this.name = "DecryptError";
    this.error = error;
  }
};
var EncryptError = class extends Error {
  constructor(error) {
    super("encrypt error");
    this.name = "EncryptError";
    this.error = error;
  }
};

// src/encryption/gcm.ts
var SALT_LENGTH = 16;
var IV_LENGTH = 12;
var KEY_LENGTH = 32;
var ITERATIONS = 2 ** 19;
var encrypt = async (content, password) => {
  try {
    const salt = randomBytes2(SALT_LENGTH);
    const key = await pbkdf2Async(nobleSha256, utf8ToBytes(password), salt, {
      c: ITERATIONS,
      dkLen: KEY_LENGTH
    });
    const iv = randomBytes2(IV_LENGTH);
    const cipher = gcm(key, iv);
    const encrypted = cipher.encrypt(utf8ToBytes(content));
    const result = concatBytes(salt, iv, encrypted);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const salt = data.subarray(0, SALT_LENGTH);
    const iv = data.subarray(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ciphertext = data.subarray(SALT_LENGTH + IV_LENGTH);
    const key = await pbkdf2Async(nobleSha256, utf8ToBytes(password), salt, {
      c: ITERATIONS,
      dkLen: KEY_LENGTH
    });
    const cipher = gcm(key, iv);
    const decrypted = cipher.decrypt(ciphertext);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/encryption/mlkem.ts
var mlkem_exports = {};
__export(mlkem_exports, {
  decrypt: () => decrypt2,
  encrypt: () => encrypt2
});
import { ml_kem768 } from "@noble/post-quantum/ml-kem";
import { randomBytes as randomBytes3 } from "@noble/post-quantum/utils";
import { concatBytes as concatBytes2, utf8ToBytes as utf8ToBytes2 } from "@noble/hashes/utils";
import { gcm as gcm2 } from "@noble/ciphers/aes";
import { sha512 } from "@noble/hashes/sha512";
var IV_LENGTH2 = 12;
var CIPHERTEXT_LENGTH = 1088;
var encrypt2 = async (content, password) => {
  try {
    const passwordBytes = utf8ToBytes2(password);
    const seed = sha512(passwordBytes);
    const derivedKeyPair = ml_kem768.keygen(seed);
    const { cipherText, sharedSecret } = ml_kem768.encapsulate(derivedKeyPair.publicKey);
    const iv = randomBytes3(IV_LENGTH2);
    const cipher = gcm2(sharedSecret, iv);
    const encryptedContent = cipher.encrypt(utf8ToBytes2(content));
    const result = concatBytes2(cipherText, iv, encryptedContent);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt2 = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const cipherText = data.subarray(0, CIPHERTEXT_LENGTH);
    const iv = data.subarray(CIPHERTEXT_LENGTH, CIPHERTEXT_LENGTH + IV_LENGTH2);
    const encrypted = data.subarray(CIPHERTEXT_LENGTH + IV_LENGTH2);
    const passwordBytes = utf8ToBytes2(password);
    const seed = sha512(passwordBytes);
    const derivedKeyPair = ml_kem768.keygen(seed);
    const sharedSecret = ml_kem768.decapsulate(cipherText, derivedKeyPair.secretKey);
    const cipher = gcm2(sharedSecret, iv);
    const decrypted = cipher.decrypt(encrypted);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/hash.ts
import { sha256 as nobleSha2562 } from "@noble/hashes/sha256";
import { sha512 as nobleSha512 } from "@noble/hashes/sha512";
var sha256 = (input) => Buffer2.from(nobleSha2562(input)).toString("hex");
var sha5122 = (input) => Buffer2.from(nobleSha512(input)).toString("hex");

// src/client.ts
var Client = class {
  constructor({
    apiUrl,
    keyLength = 32,
    encrypt: encrypt3 = gcm_exports.encrypt,
    decrypt: decrypt3 = gcm_exports.decrypt,
    xClient = void 0
  }) {
    this.apiUrl = apiUrl;
    this.keyLength = keyLength;
    this.encrypt = encrypt3;
    this.decrypt = decrypt3;
    this.xClient = xClient;
  }
  getHeaders() {
    const headers = {
      "Content-Type": "application/json"
    };
    if (this.xClient) {
      headers["X-Client"] = this.xClient;
    }
    return headers;
  }
  compressContent(content) {
    const compressed = deflate(new TextEncoder().encode(content));
    const result = {
      metadata: {
        compression: {
          algorithm: "zlib:pako"
        },
        encryption: {
          algorithm: "aes-256-gcm"
        }
      },
      data: Buffer2.from(compressed).toString("base64")
    };
    return JSON.stringify(result);
  }
  decompressContent(content) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.metadata) {
        let processedContent = Buffer2.from(parsed.data, "base64");
        if (parsed.metadata.compression?.algorithm === "zlib:pako") {
          const decompressed = inflate(processedContent);
          processedContent = Buffer2.from(decompressed);
        }
        return new TextDecoder().decode(processedContent);
      }
    } catch {
    }
    return content;
  }
  async create(input) {
    const key = await generateRandomString(this.keyLength);
    const compressed = this.compressContent(input.c);
    let encrypted = await this.encrypt(compressed, key);
    if (input.p) {
      encrypted = await this.encrypt(encrypted, input.p);
    }
    const hash = sha5122(key + (input.p ?? ""));
    const response = await fetch(`${this.apiUrl}/vault`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        c: encrypted,
        h: hash,
        b: input.b,
        ttl: input.ttl,
        ips: input.ips,
        rc: input.rc,
        fc: input.fc,
        wh: input.wh ? {
          u: input.wh.u,
          n: input.wh.n,
          r: input.wh.r,
          fpk: input.wh.fpk,
          fip: input.wh.fip,
          b: input.wh.b
        } : void 0
      })
    });
    if (!response.ok) {
      throw new ErrorUnexpectedStatus(response.status);
    }
    const data = await response.json();
    return {
      ...data,
      key,
      hash
    };
  }
  async read(id, key, password) {
    const h = sha5122(key + (password ?? ""));
    const res = await fetch(`${this.apiUrl}/vault/${id}?h=${h}`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      if (res.status === 400) {
        throw new ErrorInvalidKeyAndOrPassword();
      } else if (res.status === 404) {
        throw new ErrorNotFound();
      }
      throw new ErrorUnexpectedStatus(res.status);
    }
    const data = await res.json();
    const decrypted = password ? await this.decrypt(data.c, password).then((d) => this.decrypt(d, key)) : await this.decrypt(data.c, key);
    const decompressed = this.decompressContent(decrypted);
    return {
      c: decompressed,
      burned: data.b,
      cd: data.cd,
      ttl: data.ttl
    };
  }
  async delete(id, dt) {
    const response = await fetch(`${this.apiUrl}/vault/${id}`, {
      method: "DELETE",
      headers: this.getHeaders(),
      body: JSON.stringify({ dt })
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new ErrorNotFound();
      }
      throw new ErrorUnexpectedStatus(response.status);
    }
  }
  async exists(id) {
    const response = await fetch(`${this.apiUrl}/vault/${id}`, {
      method: "HEAD",
      headers: this.getHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) {
        return false;
      }
      throw new ErrorUnexpectedStatus(response.status);
    }
    return true;
  }
};
var ErrorNotFound = class extends Error {
  constructor() {
    super("secret not found");
  }
};
var ErrorUnexpectedStatus = class extends Error {
  constructor(status) {
    super(`unexpected status code ${status}`);
  }
};
var ErrorInvalidKeyAndOrPassword = class extends Error {
  constructor() {
    super("invalid key and/or password");
  }
};

// src/sleep.ts
var sleep = (ms, { enabled = true } = {}) => enabled ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
var sleepWithCancel = (ms, { enabled = true } = {}) => {
  if (!enabled) {
    return {
      promise: Promise.resolve(),
      cancel: () => {
      }
      // No-op for disabled sleep
    };
  }
  let timeoutId;
  const promise = new Promise((resolve) => {
    timeoutId = setTimeout(resolve, ms);
  });
  return {
    promise,
    cancel: () => {
      clearTimeout(timeoutId);
    }
  };
};

// src/fetchRetry.ts
var createFetchRetryClient = ({
  maxAttempts,
  requestTimeoutMs = 5e3,
  fetchFn = fetch,
  shouldRetry = isRetryableFetchError,
  calculateBackoff = defaultCalculateBackoff
}) => {
  return async (url, options) => {
    let attempts = 0;
    const errors = [];
    do {
      const signals = [options?.signal];
      let timeoutId;
      if (requestTimeoutMs > 0) {
        const ab = new AbortController();
        timeoutId = setTimeout(() => ab.abort(new TimeoutError()), requestTimeoutMs);
        signals.push(ab.signal);
      }
      const signal = AbortSignal.any(signals.filter((s) => !!s));
      try {
        const response = await fetchFn(url, { ...options, signal });
        if (!response.ok) {
          throw new HTTPError(response.status, response.statusText);
        }
        return response;
      } catch (error) {
        errors.push(error);
        if (!shouldRetry(error)) {
          throw new FetchRetryError(errors, "Non-retryable error occurred");
        }
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
      const backoffMs = calculateBackoff(attempts);
      const sleeper = sleepWithCancel(backoffMs);
      const eventListenerAC = new AbortController();
      await Promise.race([
        sleeper.promise,
        new Promise(
          (resolve) => signal.addEventListener("abort", resolve, { signal: eventListenerAC.signal, once: true })
        )
      ]);
      sleeper.cancel();
      eventListenerAC.abort();
    } while (++attempts < maxAttempts);
    throw new FetchRetryError(errors, "Failed to fetch after max retries");
  };
};
var isRetryableFetchError = (error) => {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof TimeoutError) {
    return true;
  }
  return error instanceof HTTPError && isRetryableStatusCode(error.status);
};
var isRetryableStatusCode = (status) => {
  if (status >= 500 && status <= 599) return true;
  if (status === 429) return true;
  return [408, 423, 425].includes(status);
};
var defaultCalculateBackoff = (attempt) => {
  const backoffMs = 5e3 * Math.pow(2, attempt - 1);
  return Math.min(backoffMs, 6e4);
};
var HTTPError = class extends Error {
  constructor(status, message) {
    super(`${status} ${message}`);
    this.status = status;
    this.name = "HTTPError";
  }
};
var TimeoutError = class extends Error {
  constructor() {
    super();
    this.name = "TimeoutError";
  }
};
var FetchRetryError = class extends Error {
  constructor(errors, message) {
    super(message);
    this.errors = errors;
    this.name = "FetchRetryError";
  }
  toString() {
    return this.errors.map((error) => error instanceof Error ? error.message : error).join("\n");
  }
};
export {
  Client,
  DecryptError,
  EncryptError,
  ErrorInvalidKeyAndOrPassword,
  ErrorNotFound,
  ErrorUnexpectedStatus,
  FetchRetryError,
  HTTPError,
  TimeoutError,
  URL_SAFE_DICTIONARY,
  createFetchRetryClient,
  createVaultRequestSchema,
  createVaultResponseSchema,
  de,
  deleteVaultParamsSchema,
  deleteVaultRequestSchema,
  en,
  es,
  fr,
  gcm_exports as gcm,
  generateRandomBytes,
  generateRandomHexString,
  generateRandomString,
  isRetryableFetchError,
  mlkem_exports as mlkem,
  readVaultParamsSchema,
  readVaultQuerySchema,
  readVaultResponseSchema,
  resources,
  sha256,
  sha5122 as sha512,
  sleep,
  sleepWithCancel,
  supportedLanguages,
  supportedLanguagesOptions,
  vaultValueSchema,
  zh
};
